/**
 * Complete Education Lifecycle A2A Dashboard
 * Version: 2.0
 * 
 * Comprehensive educational system dashboard featuring:
 * - Complete educational lifecycle management
 * - Agent-to-Agent (A2A) system simulation
 * - Real-time workflow tracking
 * - Multi-agent collaboration visualization
 * 
 * Integrates with A2A backend for intelligent education capabilities
 */

import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, 
  Users, 
  BarChart3, 
  Settings, 
  LogOut,
  Bell,
  Search,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  Play,
  Pause,
  Lightbulb,
  Network,
  Activity,
  Target,
  BookOpen,
  ClipboardCheck,
  MessageSquare,
  Star,
  Info,
  X,
  Zap,
  ArrowRight,
  FileText,
  Printer,
  Share2,
  Calendar,
  User,
  Award,
  BarChart,
  PieChart,
  FileCheck,
  Mail,
  Bookmark,
  Flag,
  Timer,
  Users2,
  Brain,
  Sparkles,
  Heart,
  GitBranch
} from 'lucide-react';

// Import components
import LearnSystemView from './components/LearnSystemView';
import AgentEcosystemView from './components/AgentEcosystemView';
import CompleteWorkflowView from './components/CompleteWorkflowView';
import LiveSimulationView from './components/LiveSimulationView';
import LoadingSpinner from './components/LoadingSpinner';
import NotificationToast from './components/NotificationToast';
import VisualFlowTab from './components/VisualFlowTab';

// Import A2A Client (temporarily disabled for build)
// import { A2AClient } from '../lib/a2a-client.js';

// Import Supabase client
import { createClient } from '@supabase/supabase-js';

// Configuration
const SUPABASE_URL = 'https://uszhqmcohbwnabxgfuhz.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVzemhxbWNvaGJ3bmFieGdmdWh6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk4NDMzMjYsImV4cCI6MjA3NTQxOTMyNn0.YNvc18MX9tchryD6N3ImzFMh3EGybG3fGkZnIuL6AWc';
const REMOTE_AGENT_URL = process.env.REACT_APP_REMOTE_AGENT_URL || 'https://a2aschoolkpi-server.appsokay.net';

// Initialize clients
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const App = () => {
  // State management
  const [activeTab, setActiveTab] = useState('simulation');
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showSimulationBanner, setShowSimulationBanner] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [a2aClient, setA2AClient] = useState(null); // Temporarily disabled
  
  // API Testing state
  const [apiResults, setApiResults] = useState({});
  const [apiLoading, setApiLoading] = useState({});
  const [capabilities, setCapabilities] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  
  // Workflow execution state
  const [workflowExecuting, setWorkflowExecuting] = useState(false);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [workflowResults, setWorkflowResults] = useState({});
  const [workflowLogs, setWorkflowLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Workflow outputs state - for realistic teacher documents
  const [workflowOutputs, setWorkflowOutputs] = useState({});
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [showOutputModal, setShowOutputModal] = useState(false);
  
  // DeepSeek API usage tracking
  const [apiUsage, setApiUsage] = useState({
    totalRequests: 0,
    totalTokens: 0,
    totalCostCNY: 0,
    totalCostUSD: 0,
    lastRequest: null,
    requestsToday: 0
  });
  
  // Simulation data
  const [simulationData, setSimulationData] = useState({
    students: 0,
    lessons: 0,
    tests: 0,
    feedback: 0,
    goals: 0,
    selfAssess: 0,
    teacherKpi: 0
  });
  
  const [agentLogs, setAgentLogs] = useState([]);
  const [workflowState, setWorkflowState] = useState([]);

  // Initialize A2A Client and discover capabilities (temporarily disabled)
  useEffect(() => {
    // Temporarily disabled for build
    setLoading(false);
    setConnectionStatus('connected');
    addNotification('A2A connection temporarily disabled for deployment', 'info');
  }, []);

  // Real-time notifications from Supabase
  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'kpi_assessments'
      }, (payload) => {
        console.log('Assessment update:', payload);
        addNotification('New assessment data available', 'info');
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'kpi_learning_goals'
      }, (payload) => {
        console.log('Learning goal update:', payload);
        addNotification('Learning goals updated', 'success');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Notification management
  const addNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };


  // Start full cycle simulation with real workflow
  const startFullCycle = async () => {
    setIsSimulationRunning(true);
    setCurrentStep(0);
    setSimulationData({
      students: 0,
      lessons: 0,
      tests: 0,
      feedback: 0,
      goals: 0,
      selfAssess: 0,
      teacherKpi: 0
    });
    setAgentLogs([]);
    setWorkflowState(workflowSteps.map(s => ({ ...s, status: 'pending' })));
    addNotification('Full cycle simulation started', 'success');
    
    // Start workflow execution
    executeWorkflow();
  };

  // Execute workflow step by step with backend communication
  const executeWorkflow = async () => {
    for (let i = 0; i < workflowSteps.length; i++) {
      if (!isSimulationRunning) break;
      
      const step = workflowSteps[i];
      setCurrentStep(i);
      
      // Update step status to 'in_progress'
      setWorkflowState(prev => prev.map((s, idx) => 
        idx === i ? { ...s, status: 'in_progress' } : s
      ));
      
      // Add agent log
      const log = {
        id: Date.now() + i,
        agent: step.agent,
        message: step.action,
        timestamp: new Date().toLocaleTimeString(),
        type: 'process',
        step: i + 1
      };
      setAgentLogs(prev => [...prev, log]);
      
      // Call backend API for this step
      try {
        await executeBackendStep(step, i);
      } catch (error) {
        console.error('Backend step failed:', error);
        addNotification(`Step ${i + 1} failed: ${error.message}`, 'error');
      }
      
      // Update simulation data based on step
      updateSimulationData(i);
      
      // Wait for step duration
      await new Promise(resolve => setTimeout(resolve, step.duration));
      
      // Update step status to 'completed'
      setWorkflowState(prev => prev.map((s, idx) => 
        idx === i ? { ...s, status: 'completed' } : s
      ));
    }
    
    // Simulation complete
    setIsSimulationRunning(false);
    addNotification('Full educational cycle completed!', 'success');
  };

  // Execute backend step
  const executeBackendStep = async (step, stepIndex) => {
    const backendUrl = 'https://a2aschoolkpi-server.appsokay.net';
    
    // Map workflow steps to backend capabilities
    let capability = '';
    let parameters = {};
    
    switch (stepIndex) {
      case 0: // Teacher Prepares Lesson
        capability = 'assess_student';
        parameters = {
          student_id: 'demo_student_001',
          assessment_data: {
            subject: 'Mathematics',
            topic: 'Quadratic Equations',
            lesson_type: 'preparation'
          }
        };
        break;
      case 2: // Create Assessments
        capability = 'assess_student';
        parameters = {
          student_id: 'demo_student_001',
          assessment_data: {
            subject: 'Mathematics',
            topic: 'Quadratic Equations',
            assessment_type: 'quiz'
          }
        };
        break;
      case 5: // Generate Student Feedback
        capability = 'generate_adaptive_goals';
        parameters = {
          student_id: 'demo_student_001'
        };
        break;
      case 9: // Teacher KPI Calculation
        capability = 'calculate_teacher_kpi';
        parameters = {
          teacher_id: 'demo_teacher_001',
          period: 'current_semester'
        };
        break;
      default:
        // For other steps, just simulate without backend call
        return;
    }
    
    if (capability) {
      const response = await fetch(`${backendUrl}/a2a/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          capability,
          parameters
        })
      });
      
      if (!response.ok) {
        throw new Error(`Backend request failed: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`Step ${stepIndex + 1} backend result:`, result);
      
      // Add backend response to agent logs
      const backendLog = {
        id: Date.now() + i + 1000,
        agent: `${step.agent} (Backend)`,
        message: `Backend response: ${JSON.stringify(result.result || result)}`,
        timestamp: new Date().toLocaleTimeString(),
        type: 'success',
        step: stepIndex + 1
      };
      setAgentLogs(prev => [...prev, backendLog]);
    }
  };

  // Update simulation data based on current step
  const updateSimulationData = (stepIndex) => {
    setSimulationData(prev => {
      const updates = { ...prev };
      switch (stepIndex) {
        case 0: // Teacher Prepares Lesson
          updates.lessons = 1;
          break;
        case 1: // Lesson Delivery
          updates.students = 25;
          break;
        case 2: // Create Assessments
          updates.tests = 3;
          break;
        case 3: // Students Take Assessments
          updates.tests = 3;
          break;
        case 4: // Grade Results
          updates.tests = 3;
          break;
        case 5: // Generate Feedback
          updates.feedback = 25;
          break;
        case 6: // Set Goals
          updates.goals = 25;
          break;
        case 7: // Self-Assessment
          updates.selfAssess = 25;
          break;
        case 9: // Teacher KPI
          updates.teacherKpi = 1;
          break;
      }
      return updates;
    });
  };

  // Stop simulation
  const stopSimulation = () => {
    setIsSimulationRunning(false);
    addNotification('Simulation stopped', 'info');
  };

  // API Testing functions
  const testAPI = async (endpoint, method = 'GET', body = null) => {
    const backendUrl = 'https://a2aschoolkpi-server.appsokay.net';
    const url = `${backendUrl}${endpoint}`;
    
    setApiLoading(prev => ({ ...prev, [endpoint]: true }));
    
    try {
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      };
      
      if (body) {
        options.body = JSON.stringify(body);
      }
      
      const response = await fetch(url, options);
      const data = await response.json();
      
      setApiResults(prev => ({
        ...prev,
        [endpoint]: {
          status: response.status,
          statusText: response.statusText,
          data: data,
          timestamp: new Date().toLocaleTimeString()
        }
      }));
      
      addNotification(`${method} ${endpoint} - Status: ${response.status}`, response.ok ? 'success' : 'error');
      
    } catch (error) {
      setApiResults(prev => ({
        ...prev,
        [endpoint]: {
          status: 'ERROR',
          statusText: 'Network Error',
          data: { error: error.message },
          timestamp: new Date().toLocaleTimeString()
        }
      }));
      
      addNotification(`${method} ${endpoint} - Error: ${error.message}`, 'error');
    } finally {
      setApiLoading(prev => ({ ...prev, [endpoint]: false }));
    }
  };

  const testCapability = async (capability, parameters) => {
    await testAPI('/a2a/execute', 'POST', {
      capability,
      parameters
    });
  };

  const discoverCapabilities = async () => {
    await testAPI('/a2a/discover');
  };

  const testHealth = async () => {
    await testAPI('/health');
  };

  const testUXNegotiation = async () => {
    await testAPI('/a2a/negotiate-ux');
  };

  // Workflow execution functions
  const workflowSteps = [
    {
      id: 1,
      name: 'prepare_lesson',
      title: 'Teacher Prepares Lesson',
      description: 'AI-powered lesson preparation with comprehensive planning',
      icon: <BookOpen className="w-4 h-4" />,
      color: 'blue',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1,
        duration_minutes: 30,
        class_size: 25
      }
    },
    {
      id: 2,
      name: 'deliver_lesson',
      title: 'Lesson Delivery',
      description: 'AI-guided lesson delivery with engagement strategies',
      icon: <Users className="w-4 h-4" />,
      color: 'green',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1,
        class_size: 25,
        class_name: '1A'
      }
    },
    {
      id: 3,
      name: 'create_assessments',
      title: 'Create Assessments',
      description: 'AI-generated comprehensive assessments with rubrics',
      icon: <ClipboardCheck className="w-4 h-4" />,
      color: 'purple',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1
      }
    },
    {
      id: 4,
      name: 'administer_assessments',
      title: 'Students Take Assessments',
      description: 'AI-guided assessment administration and monitoring',
      icon: <Activity className="w-4 h-4" />,
      color: 'orange',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        student_count: 25,
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        assessment_type: 'quiz',
        duration_minutes: 30,
        grade_level: 1,
        class_name: '1A'
      }
    },
    {
      id: 5,
      name: 'grade_assessments',
      title: 'Grade and Analyze Results',
      description: 'AI-powered grading and performance analysis',
      icon: <BarChart3 className="w-4 h-4" />,
      color: 'red',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        assessment_count: 25,
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        assessment_type: 'quiz',
        total_questions: 10,
        grade_level: 1,
        use_rubric: true
      }
    },
    {
      id: 6,
      name: 'generate_student_feedback',
      title: 'Generate Student Feedback',
      description: 'AI-personalized feedback for each student',
      icon: <MessageSquare className="w-4 h-4" />,
      color: 'indigo',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        student_count: 25,
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1,
        average_score: 78,
        score_range: '50-95%'
      }
    },
    {
      id: 7,
      name: 'set_adaptive_goals',
      title: 'Set Adaptive Learning Goals',
      description: 'AI-created personalized learning objectives',
      icon: <Target className="w-4 h-4" />,
      color: 'teal',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        student_count: 25,
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1
      }
    },
    {
      id: 8,
      name: 'student_self_assessment',
      title: 'Student Self-Assessment',
      description: 'AI-guided student reflection and self-evaluation',
      icon: <Eye className="w-4 h-4" />,
      color: 'pink',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        student_count: 25,
        subject: 'Mathematics',
        topic: 'Basic Addition and Subtraction',
        grade_level: 1
      }
    },
    {
      id: 9,
      name: 'monitor_progress',
      title: 'Monitor Progress',
      description: 'AI-powered progress tracking and analysis',
      icon: <TrendingUp className="w-4 h-4" />,
      color: 'cyan',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        student_count: 25,
        subject: 'Mathematics',
        grade_level: 1,
        monitoring_period: 'current_semester'
      }
    },
    {
      id: 10,
      name: 'calculate_teacher_kpi',
      title: 'Calculate Teacher KPI',
      description: 'AI-analyzed teacher performance metrics',
      icon: <Star className="w-4 h-4" />,
      color: 'yellow',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        period: 'current_semester',
        subject: 'Mathematics',
        years_experience: 13,
        class_size: 25,
        current_performance: 'good'
      }
    },
    {
      id: 11,
      name: 'administrative_review',
      title: 'Administrative Review',
      description: 'AI-generated comprehensive administrative report',
      icon: <Settings className="w-4 h-4" />,
      color: 'gray',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        subject: 'Mathematics',
        period: 'current_semester',
        grade_level: 1,
        class_size: 25
      }
    },
    {
      id: 12,
      name: 'continuous_improvement',
      title: 'Continuous Improvement',
      description: 'AI-driven system optimization and recommendations',
      icon: <Lightbulb className="w-4 h-4" />,
      color: 'emerald',
      parameters: {
        teacher_id: '448b00ff-8f4f-41fb-9584-3cd6f6dd0b39',
        subject: 'Mathematics',
        period: 'current_semester',
        grade_level: 1
      }
    }
  ];

  const executeWorkflowStep = async (step) => {
    const endpoint = '/a2a/execute';
    const body = {
      capability: step.name,
      parameters: step.parameters
    };

    setApiLoading(prev => ({ ...prev, [step.name]: true }));
    
    const logEntry = {
      id: Date.now(),
      step: step.id,
      name: step.name,
      title: step.title,
      status: 'running',
      timestamp: new Date().toLocaleTimeString(),
      message: `Executing ${step.title}...`
    };
    
    setWorkflowLogs(prev => [...prev, logEntry]);

    try {
      // Use the correct backend URL
      const backendUrl = 'https://a2aschoolkpi-server.appsokay.net';
      const url = `${backendUrl}${endpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      // Store in API results for the response viewer
      setApiResults(prev => ({
        ...prev,
        [endpoint]: {
          status: response.status,
          statusText: response.statusText,
          data: data,
          timestamp: new Date().toLocaleTimeString()
        }
      }));
      
      if (response.ok && data && data.payload && data.payload.result && data.payload.result.success) {
        setWorkflowResults(prev => ({
          ...prev,
          [step.name]: data.payload.result.data
        }));
        
        // Generate realistic workflow outputs for teachers
        const output = generateWorkflowOutput(step.name, data.payload.result.data);
        if (output) {
          setWorkflowOutputs(prev => ({
            ...prev,
            [step.name]: output
          }));

          // Save each generated document to database
          if (output.documents && output.documents.length > 0) {
            for (const doc of output.documents) {
              await saveDocumentToDatabase({
                type: step.name,
                name: doc.name,
                content: doc.content,
                metadata: {
                  step_title: step.title,
                  generated_at: new Date().toISOString(),
                  ai_agent: data.payload.result.data.ai_agent || 'Unknown',
                  workflow_step: step.id
                }
              });
            }
            console.log(`Saved ${output.documents.length} documents to database for ${step.name}`);
          }
        }
        
        const successLog = {
          ...logEntry,
          status: 'completed',
          message: `${step.title} completed successfully - Documents generated and saved!`,
          result: data.payload.result.data
        };
        
        setWorkflowLogs(prev => prev.map(log => 
          log.id === logEntry.id ? successLog : log
        ));
        
        addNotification(`${step.title} completed successfully - Check documents!`, 'success');
        
        // Update API usage tracking
        updateApiUsage(500); // Estimate 500 tokens per request
      } else {
        throw new Error(data?.payload?.result?.message || 'Workflow step failed');
      }
      
    } catch (error) {
      const errorLog = {
        ...logEntry,
        status: 'failed',
        message: `${step.title} failed: ${error.message}`
      };
      
      setWorkflowLogs(prev => prev.map(log => 
        log.id === logEntry.id ? errorLog : log
      ));
      
      addNotification(`${step.title} failed: ${error.message}`, 'error');
    } finally {
      setApiLoading(prev => ({ ...prev, [step.name]: false }));
    }
  };

  const executeSequentialWorkflow = async () => {
    setWorkflowExecuting(true);
    setWorkflowStep(0);
    setWorkflowLogs([]);
    setWorkflowResults({});
    
    addNotification('Starting sequential workflow execution...', 'info');
    
    for (let i = 0; i < workflowSteps.length; i++) {
      if (!workflowExecuting) break; // Allow stopping
      
      setWorkflowStep(i + 1);
      await executeWorkflowStep(workflowSteps[i]);
      
      // Add delay between steps for realistic simulation
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    setWorkflowExecuting(false);
    setWorkflowStep(0);
    addNotification('Sequential workflow execution completed!', 'success');
  };

  const stopWorkflow = () => {
    setWorkflowExecuting(false);
    setWorkflowStep(0);
    addNotification('Workflow execution stopped', 'warning');
  };

  // Save generated document to database
  const saveDocumentToDatabase = async (documentData) => {
    try {
      const { data, error } = await supabase
        .from('kpi_documents')
        .insert({
          document_type: documentData.type,
          document_name: documentData.name,
          content: documentData.content,
          metadata: documentData.metadata || {},
          file_format: 'markdown',
          created_at: new Date().toISOString()
        })
        .select();

      if (error) {
        console.error('Error saving document:', error);
        return null;
      }

      console.log('Document saved successfully:', data);
      return data[0];
    } catch (error) {
      console.error('Error in saveDocumentToDatabase:', error);
      return null;
    }
  };

  // Generate realistic workflow outputs for teachers
  const generateWorkflowOutput = (stepName, stepData) => {
    // Use AI-generated content from backend if available
    const aiContent = stepData.lesson_plan || stepData.delivery_plan || stepData.assessment_content || 
                      stepData.detailed_analysis || stepData.personalized_feedback || stepData.detailed_goals ||
                      stepData.administrative_review || stepData.improvement_analysis || stepData.self_assessment_tools ||
                      stepData.progress_analysis || stepData.administration_plan || stepData.grading_analysis || '';

    const outputs = {
      prepare_lesson: {
        title: "Lesson Plan & Materials",
        icon: <FileText className="w-6 h-6" />,
        documents: [
          {
            name: "Complete Lesson Plan",
            type: "PDF",
            content: stepData.lesson_plan || aiContent || generateLessonPlan(stepData),
            icon: <BookOpen className="w-4 h-4" />
          },
          {
            name: "Materials Checklist",
            type: "PDF", 
            content: stepData.lesson_plan || aiContent || generateMaterialsChecklist(stepData),
            icon: <ClipboardCheck className="w-4 h-4" />
          },
          {
            name: "Learning Objectives",
            type: "PDF",
            content: stepData.lesson_plan || aiContent || generateLearningObjectives(stepData),
            icon: <Target className="w-4 h-4" />
          }
        ]
      },
      deliver_lesson: {
        title: "Delivery Guide & Resources",
        icon: <Users2 className="w-6 h-6" />,
        documents: [
          {
            name: "Interactive Delivery Guide",
            type: "PDF",
            content: stepData.delivery_plan || aiContent || generateDeliveryGuide(stepData),
            icon: <BookOpen className="w-4 h-4" />
          },
          {
            name: "Presentation Slides",
            type: "PDF",
            content: stepData.delivery_plan || aiContent || generatePresentationSlides(stepData),
            icon: <Sparkles className="w-4 h-4" />
          },
          {
            name: "Classroom Management Tips",
            type: "PDF",
            content: stepData.delivery_plan || aiContent || generateClassroomTips(stepData),
            icon: <Users className="w-4 h-4" />
          }
        ]
      },
      create_assessments: {
        title: "Assessment Tools & Rubrics",
        icon: <ClipboardCheck className="w-6 h-6" />,
        documents: [
          {
            name: "Assessment Sheets",
            type: "PDF",
            content: stepData.assessments || stepData.assessment_content || aiContent || generateAssessmentSheets(stepData),
            icon: <FileText className="w-4 h-4" />
          },
          {
            name: "Grading Rubric",
            type: "PDF",
            content: stepData.assessments || stepData.assessment_content || aiContent || generateGradingRubric(stepData),
            icon: <BarChart className="w-4 h-4" />
          },
          {
            name: "Answer Key",
            type: "PDF",
            content: stepData.assessments || stepData.assessment_content || aiContent || generateAnswerKey(stepData),
            icon: <CheckCircle className="w-4 h-4" />
          }
        ]
      },
      administer_assessments: {
        title: "Administration Guide",
        icon: <Timer className="w-6 h-6" />,
        documents: [
          {
            name: "Administration Checklist",
            type: "PDF",
            content: stepData.administration_plan || aiContent || generateAdminChecklist(stepData),
            icon: <ClipboardCheck className="w-4 h-4" />
          },
          {
            name: "Classroom Setup Guide",
            type: "PDF",
            content: stepData.administration_plan || aiContent || generateClassroomSetup(stepData),
            icon: <Users className="w-4 h-4" />
          },
          {
            name: "Accommodation Guidelines",
            type: "PDF",
            content: stepData.administration_plan || aiContent || generateAccommodationGuide(stepData),
            icon: <Heart className="w-4 h-4" />
          }
        ]
      },
      grade_assessments: {
        title: "Grading & Analysis Report",
        icon: <BarChart className="w-6 h-6" />,
        documents: [
          {
            name: "Detailed Grading Report",
            type: "PDF",
            content: stepData.grading_analysis || aiContent || generateGradingReport(stepData),
            icon: <FileText className="w-4 h-4" />
          },
          {
            name: "Class Performance Analytics",
            type: "PDF",
            content: stepData.grading_analysis || aiContent || generatePerformanceAnalytics(stepData),
            icon: <PieChart className="w-4 h-4" />
          },
          {
            name: "Learning Gap Analysis",
            type: "PDF",
            content: stepData.grading_analysis || aiContent || generateGapAnalysis(stepData),
            icon: <Target className="w-4 h-4" />
          }
        ]
      },
      generate_student_feedback: {
        title: "Student Feedback Package",
        icon: <MessageSquare className="w-6 h-6" />,
        documents: [
          {
            name: "Individual Feedback Cards",
            type: "PDF",
            content: stepData.personalized_feedback || stepData.studentFeedbackBatch || aiContent || generateFeedbackCards(stepData),
            icon: <Mail className="w-4 h-4" />
          },
          {
            name: "Parent Communication Templates",
            type: "PDF",
            content: stepData.personalized_feedback || stepData.studentFeedbackBatch || aiContent || generateParentTemplates(stepData),
            icon: <Users className="w-4 h-4" />
          },
          {
            name: "Strengths & Improvement Summary",
            type: "PDF",
            content: stepData.personalized_feedback || stepData.studentFeedbackBatch || aiContent || generateStrengthsSummary(stepData),
            icon: <Award className="w-4 h-4" />
          }
        ]
      },
      set_adaptive_goals: {
        title: "Personalized Learning Goals",
        icon: <Target className="w-6 h-6" />,
        documents: [
          {
            name: "Individual Goal Sheets",
            type: "PDF",
            content: stepData.adaptive_goals || stepData.detailed_goals || aiContent || generateGoalSheets(stepData),
            icon: <Flag className="w-4 h-4" />
          },
          {
            name: "Goal Tracking Templates",
            type: "PDF",
            content: stepData.adaptive_goals || stepData.detailed_goals || aiContent || generateGoalTracking(stepData),
            icon: <Calendar className="w-4 h-4" />
          },
          {
            name: "Achievement Timeline",
            type: "PDF",
            content: stepData.adaptive_goals || stepData.detailed_goals || aiContent || generateAchievementTimeline(stepData),
            icon: <Clock className="w-4 h-4" />
          }
        ]
      },
      student_self_assessment: {
        title: "Self-Assessment Tools",
        icon: <Brain className="w-6 h-6" />,
        documents: [
          {
            name: "Self-Reflection Worksheets",
            type: "PDF",
            content: stepData.self_assessment_tools || stepData.selfAssessment || aiContent || generateSelfReflectionSheets(stepData),
            icon: <FileText className="w-4 h-4" />
          },
          {
            name: "Guided Question Prompts",
            type: "PDF",
            content: stepData.self_assessment_tools || stepData.selfAssessment || aiContent || generateQuestionPrompts(stepData),
            icon: <MessageSquare className="w-4 h-4" />
          },
          {
            name: "Self-Assessment Rubric",
            type: "PDF",
            content: stepData.self_assessment_tools || stepData.selfAssessment || aiContent || generateSelfAssessmentRubric(stepData),
            icon: <BarChart className="w-4 h-4" />
          }
        ]
      },
      monitor_progress: {
        title: "Progress Monitoring Dashboard",
        icon: <TrendingUp className="w-6 h-6" />,
        documents: [
          {
            name: "Visual Progress Dashboard",
            type: "PDF",
            content: stepData.progress_analysis || stepData.progressAnalysis || aiContent || generateProgressDashboard(stepData),
            icon: <PieChart className="w-4 h-4" />
          },
          {
            name: "Progress Tracking Spreadsheet",
            type: "Excel",
            content: stepData.progress_analysis || stepData.progressAnalysis || aiContent || generateProgressSpreadsheet(stepData),
            icon: <BarChart className="w-4 h-4" />
          },
          {
            name: "Alert System Guidelines",
            type: "PDF",
            content: stepData.progress_analysis || stepData.progressAnalysis || aiContent || generateAlertGuidelines(stepData),
            icon: <AlertCircle className="w-4 h-4" />
          }
        ]
      },
      calculate_teacher_kpi: {
        title: "Teacher Performance Report",
        icon: <Star className="w-6 h-6" />,
        documents: [
          {
            name: "KPI Performance Report",
            type: "PDF",
            content: stepData.detailed_analysis || stepData.kpiAnalysis || aiContent || generateKPIPerformanceReport(stepData),
            icon: <BarChart className="w-4 h-4" />
          },
          {
            name: "Achievement Summary",
            type: "PDF",
            content: stepData.detailed_analysis || stepData.kpiAnalysis || aiContent || generateAchievementSummary(stepData),
            icon: <Award className="w-4 h-4" />
          },
          {
            name: "Improvement Recommendations",
            type: "PDF",
            content: stepData.detailed_analysis || stepData.kpiAnalysis || aiContent || generateImprovementRecommendations(stepData),
            icon: <Lightbulb className="w-4 h-4" />
          }
        ]
      },
      administrative_review: {
        title: "Administrative Review Package",
        icon: <Settings className="w-6 h-6" />,
        documents: [
          {
            name: "Comprehensive Administrative Report",
            type: "PDF",
            content: stepData.administrative_review || stepData.adminReview || aiContent || generateAdminReport(stepData),
            icon: <FileText className="w-4 h-4" />
          },
          {
            name: "School Performance Summary",
            type: "PDF",
            content: stepData.administrative_review || stepData.adminReview || aiContent || generateSchoolPerformanceSummary(stepData),
            icon: <BarChart className="w-4 h-4" />
          },
          {
            name: "Strategic Recommendations",
            type: "PDF",
            content: stepData.administrative_review || stepData.adminReview || aiContent || generateStrategicRecommendations(stepData),
            icon: <Target className="w-4 h-4" />
          }
        ]
      },
      continuous_improvement: {
        title: "Continuous Improvement Plan",
        icon: <RefreshCw className="w-6 h-6" />,
        documents: [
          {
            name: "Action Plan Template",
            type: "PDF",
            content: stepData.improvement_analysis || stepData.improvementAnalysis || aiContent || generateActionPlan(stepData),
            icon: <Calendar className="w-4 h-4" />
          },
          {
            name: "Process Improvement Suggestions",
            type: "PDF",
            content: stepData.improvement_analysis || stepData.improvementAnalysis || aiContent || generateProcessImprovements(stepData),
            icon: <Lightbulb className="w-4 h-4" />
          },
          {
            name: "Implementation Timeline",
            type: "PDF",
            content: stepData.improvement_analysis || stepData.improvementAnalysis || aiContent || generateImplementationTimeline(stepData),
            icon: <Clock className="w-4 h-4" />
          }
        ]
      }
    };

    return outputs[stepName] || null;
  };

  // Document generation functions (simplified for demo)
  const generateLessonPlan = (data) => {
    return `
# COMPREHENSIVE LESSON PLAN
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${data.topic || 'Basic Addition and Subtraction'}
**Grade Level:** ${data.grade_level || 1}
**Duration:** ${data.duration_minutes || 30} minutes
**Class Size:** ${data.student_count || 25} students

## Learning Objectives
${data.learning_objectives?.map(obj => `• ${obj}`).join('\n') || '• Understand basic mathematical concepts\n• Apply learned skills in practical situations'}

## Materials Needed
${data.materials_needed?.map(material => `• ${material}`).join('\n') || '• Whiteboard and markers\n• Student worksheets\n• Interactive examples'}

## Lesson Structure
1. **Warm-up (5 minutes):** Review previous concepts
2. **Introduction (10 minutes):** Present new material
3. **Practice (15 minutes):** Guided and independent practice
4. **Assessment (5 minutes):** Quick check for understanding

## Assessment Methods
• Formative assessment during practice
• Exit ticket for understanding check
• Peer review activities

## Differentiation Strategies
• Visual aids for visual learners
• Hands-on activities for kinesthetic learners
• Extended time for students who need it

---
*Generated by A2A School KPI System - Teaching Delivery Agent*
    `.trim();
  };

  const generateMaterialsChecklist = (data) => {
    return `
# MATERIALS CHECKLIST
**Lesson:** ${data.topic || 'Basic Addition and Subtraction'}
**Date:** ${new Date().toLocaleDateString()}

## Required Materials
${data.materials_needed?.map((material, index) => `${index + 1}. [ ] ${material}`).join('\n') || 
`1. [ ] Whiteboard and markers
2. [ ] Student worksheets
3. [ ] Interactive examples
4. [ ] Assessment sheets`}

## Technology Requirements
- [ ] Computer/projector setup
- [ ] Internet connection (if needed)
- [ ] Audio system (if needed)

## Backup Materials
- [ ] Extra worksheets
- [ ] Alternative activities
- [ ] Emergency lesson plan

## Pre-Lesson Setup
- [ ] Classroom arranged appropriately
- [ ] Materials distributed
- [ ] Technology tested
- [ ] Backup plan ready

---
*Check all items before starting the lesson*
    `.trim();
  };

  const generateLearningObjectives = (data) => {
    return `
# LEARNING OBJECTIVES
**Subject:** ${data.subject || 'Mathematics'}
**Grade Level:** ${data.grade_level || 1}

## Primary Objectives
${data.learning_objectives?.map((obj, index) => `${index + 1}. ${obj}`).join('\n') || 
`1. Understand basic mathematical concepts
2. Apply learned skills in practical situations
3. Demonstrate problem-solving abilities`}

## Success Criteria
Students will be able to:
• Explain the concept clearly
• Solve problems independently
• Apply knowledge in new situations
• Work collaboratively with peers

## Assessment Alignment
Each objective will be assessed through:
• Direct observation
• Student work samples
• Peer and self-assessment
• Formal assessment tools

---
*Display these objectives in the classroom for student reference*
    `.trim();
  };

  // Enhanced document generators with realistic content
  const generateDeliveryGuide = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const duration = data.duration_minutes || 30;
    const gradeLevel = data.grade_level || 1;
    
    return `# INTERACTIVE DELIVERY GUIDE
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Duration:** ${duration} minutes
**Class Size:** ${data.student_count || 25} students

## PRE-LESSON PREPARATION
### Materials Setup (5 minutes before class)
- [ ] Whiteboard/projector ready with topic title
- [ ] All worksheets and materials distributed
- [ ] Technology tested and backup plan ready
- [ ] Classroom arranged for optimal engagement

### Engagement Strategies
**Visual Learners:**
• Use colorful charts and diagrams
• Display key concepts on whiteboard
• Provide visual examples and illustrations

**Auditory Learners:**
• Use verbal explanations and discussions
• Incorporate songs or rhymes for memorization
• Encourage verbal participation and questions

**Kinesthetic Learners:**
• Include hands-on activities and manipulatives
• Allow movement and physical demonstrations
• Use interactive games and physical examples

## DETAILED TIMING GUIDE

### Phase 1: Warm-up & Introduction (${Math.floor(duration * 0.15)} minutes)
**Objectives:**
• Activate prior knowledge
• Create interest and engagement
• Set clear learning expectations

**Activities:**
• Quick review of previous lesson (3 minutes)
• Real-world connection to topic (2 minutes)
• Learning objectives overview (2 minutes)
• Engagement hook/attention grabber (3 minutes)

### Phase 2: Direct Instruction (${Math.floor(duration * 0.4)} minutes)
**Objectives:**
• Present new concepts clearly
• Model problem-solving strategies
• Check for understanding frequently

**Teaching Methods:**
• Interactive lecture with student participation
• Think-aloud demonstrations
• Guided practice with immediate feedback
• Visual aids and concrete examples

### Phase 3: Guided Practice (${Math.floor(duration * 0.3)} minutes)
**Objectives:**
• Allow students to practice with support
• Identify and address misconceptions
• Build confidence through success

**Activities:**
• Partner work with teacher monitoring
• Small group problem-solving
• Individual practice with teacher assistance
• Peer tutoring opportunities

### Phase 4: Independent Practice & Assessment (${Math.floor(duration * 0.1)} minutes)
**Objectives:**
• Assess individual understanding
• Provide immediate feedback
• Prepare for next lesson

**Activities:**
• Individual worksheet completion
• Quick exit ticket assessment
• Self-reflection questions
• Preview of next lesson

## CLASSROOM MANAGEMENT STRATEGIES

### Attention Management
• Use proximity control to maintain focus
• Implement "3-2-1" attention signals
• Vary voice tone and pace to maintain interest
• Use positive reinforcement frequently

### Behavior Management
• Establish clear expectations at lesson start
• Use non-verbal cues for redirection
• Implement "catch them being good" strategy
• Address disruptions quickly and privately

### Differentiation Strategies
**For Advanced Students:**
• Provide extension activities
• Allow peer tutoring opportunities
• Offer challenging problem-solving tasks

**For Struggling Students:**
• Provide additional visual supports
• Offer one-on-one assistance
• Break tasks into smaller steps
• Use manipulatives and concrete examples

## ASSESSMENT & FEEDBACK

### Formative Assessment Techniques
• Thumbs up/down for understanding checks
• Exit tickets with key questions
• Observation notes during activities
• Quick verbal comprehension checks

### Feedback Strategies
• Immediate positive reinforcement
• Specific, actionable improvement suggestions
• Peer feedback opportunities
• Self-assessment reflection prompts

## TRANSITION PLANNING

### To Next Activity
• Clear transition signals and timing
• Materials preparation for next phase
• Student movement and positioning
• Attention refocusing techniques

### To Next Lesson
• Summary of key concepts learned
• Preview of upcoming topics
• Homework or practice assignments
• Connection to real-world applications

---
*Generated by A2A School KPI System - Teaching Delivery Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generatePresentationSlides = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    
    return `# PRESENTATION SLIDES
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Duration:** ${data.duration_minutes || 30} minutes

## SLIDE 1: WELCOME & OBJECTIVES
**Title:** Welcome to ${topic}!
**Content:**
• Today we will learn about ${topic}
• By the end of this lesson, you will be able to:
  - Understand the basic concepts
  - Solve problems independently
  - Apply knowledge in real situations
• Let's have fun learning together!

**Visual Elements:**
• Colorful title with topic theme
• Learning objectives in bullet points
• Engaging background image related to topic

## SLIDE 2: REAL-WORLD CONNECTION
**Title:** Why is ${topic} Important?
**Content:**
• Examples from daily life:
  - Shopping and money
  - Cooking and measurements
  - Sports and games
  - Building and construction
• "Think-Pair-Share": Where do you see this in your life?

**Visual Elements:**
• Real-world photos and examples
• Interactive question prompts
• Student engagement opportunities

## SLIDE 3: KEY CONCEPTS
**Title:** Understanding ${topic}
**Content:**
• Main concept explanation with simple language
• Step-by-step process breakdown
• Visual representations and diagrams
• Common mistakes to avoid

**Visual Elements:**
• Clear, simple diagrams
• Step-by-step visual process
• Color-coded information
• Interactive elements for engagement

## SLIDE 4: EXAMPLES & PRACTICE
**Title:** Let's Practice Together!
**Content:**
• Worked examples with teacher explanation
• Guided practice problems
• Student participation opportunities
• Immediate feedback and correction

**Visual Elements:**
• Large, clear problem examples
• Step-by-step solution process
• Interactive practice elements
• Success celebration animations

## SLIDE 5: INDEPENDENT PRACTICE
**Title:** Your Turn to Practice!
**Content:**
• Individual practice problems
• Varied difficulty levels
• Self-check opportunities
• Teacher support available

**Visual Elements:**
• Clear, readable practice problems
• Progress tracking elements
• Help and support buttons
• Encouraging messages

## SLIDE 6: SUMMARY & NEXT STEPS
**Title:** What We Learned Today
**Content:**
• Key concepts review
• Success celebration
• Preview of next lesson
• Homework or practice assignments

**Visual Elements:**
• Summary of key points
• Achievement celebration
• Next lesson preview
• Clear homework instructions

## PRESENTATION TIPS

### Delivery Techniques
• Speak clearly and at appropriate pace
• Use gestures and body language effectively
• Maintain eye contact with all students
• Vary voice tone to maintain interest

### Interaction Strategies
• Ask questions frequently
• Encourage student participation
• Use wait time effectively
• Provide positive reinforcement

### Technology Management
• Test all technology before lesson
• Have backup plans ready
• Ensure all students can see and hear
• Use technology to enhance, not replace, teaching

---
*Generated by A2A School KPI System - Teaching Delivery Agent*
    `.trim();
  };

  const generateClassroomTips = (data) => {
    const gradeLevel = data.grade_level || 1;
    const classSize = data.student_count || 25;
    
    return `# CLASSROOM MANAGEMENT TIPS
**Grade Level:** ${gradeLevel}
**Class Size:** ${classSize} students
**Subject:** ${data.subject || 'Mathematics'}

## PHYSICAL ENVIRONMENT SETUP

### Seating Arrangements
**For Group Work:**
• Arrange desks in clusters of 4-6 students
• Ensure all students can see the board
• Create clear pathways for teacher movement
• Consider proximity to materials and resources

**For Individual Work:**
• Traditional rows facing the front
• Adequate space between students
• Clear view of instructional materials
• Easy access to supplies and resources

### Visual Environment
• Display learning objectives prominently
• Use anchor charts and reference materials
• Organize materials in labeled containers
• Create a welcoming, organized atmosphere

## BEHAVIOR MANAGEMENT STRATEGIES

### Prevention Strategies
**Clear Expectations:**
• Establish and post classroom rules
• Review expectations at lesson start
• Use positive language in rule statements
• Model expected behaviors consistently

**Routine Establishment:**
• Consistent daily routines and procedures
• Clear transition signals and timing
• Predictable lesson structure
• Regular schedule for activities

### Intervention Strategies
**Positive Reinforcement:**
• Catch students being good frequently
• Use specific praise for desired behaviors
• Implement reward systems and incentives
• Celebrate individual and group successes

**Redirection Techniques:**
• Use proximity control to maintain focus
• Implement non-verbal cues and signals
• Address disruptions quickly and privately
• Focus on the behavior, not the student

## ATTENTION MANAGEMENT

### Getting Attention
**Attention Signals:**
• "3-2-1, eyes on me" with hand signals
• Clapping patterns for student response
• Chime or bell for quiet attention
• "If you can hear me, clap once" technique

**Maintaining Attention:**
• Vary activities every 10-15 minutes
• Use movement and physical activities
• Incorporate student interests and choices
• Provide clear, engaging instruction

### Engagement Strategies
**Active Participation:**
• Think-pair-share activities
• Thumbs up/down for understanding checks
• Random student selection for responses
• Group discussions and peer interactions

**Varied Instruction:**
• Visual, auditory, and kinesthetic activities
• Individual, partner, and group work
• Technology integration when appropriate
• Hands-on manipulatives and materials

## DIFFERENTIATION & SUPPORT

### Learning Style Accommodations
**Visual Learners:**
• Use charts, graphs, and diagrams
• Provide written instructions and examples
• Use color coding and visual organization
• Display key information prominently

**Auditory Learners:**
• Use verbal explanations and discussions
• Incorporate music, rhymes, or chants
• Provide audio recordings when possible
• Encourage verbal participation and questions

**Kinesthetic Learners:**
• Include hands-on activities and manipulatives
• Allow movement and physical demonstrations
• Use interactive games and physical examples
• Provide opportunities for active participation

### Individual Support Strategies
**For Advanced Students:**
• Provide extension activities and challenges
• Allow peer tutoring opportunities
• Offer leadership roles in group activities
• Encourage independent research and projects

**For Struggling Students:**
• Provide additional visual supports and examples
• Offer one-on-one assistance during practice
• Break tasks into smaller, manageable steps
• Use manipulatives and concrete examples
• Provide extra time and support as needed

## TRANSITION MANAGEMENT

### Between Activities
• Clear transition signals and timing
• Materials preparation and organization
• Student movement and positioning
• Attention refocusing techniques

### Between Lessons
• Summary of key concepts learned
• Preview of upcoming topics
• Homework or practice assignments
• Connection to real-world applications

## EMERGENCY PROCEDURES

### Technology Issues
• Always have backup plans ready
• Test technology before lessons
• Prepare alternative activities
• Know how to troubleshoot common problems

### Behavior Emergencies
• Stay calm and professional
• Follow school emergency procedures
• Document incidents appropriately
• Seek support from administration when needed

## COMMUNICATION STRATEGIES

### With Students
• Use clear, age-appropriate language
• Provide specific, actionable feedback
• Listen actively to student concerns
• Maintain positive, encouraging tone

### With Parents
• Regular communication about progress
• Share positive achievements and successes
• Address concerns promptly and professionally
• Provide resources and support suggestions

---
*Generated by A2A School KPI System - Teaching Delivery Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateAssessmentSheets = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    const assessmentType = data.assessment_type || 'quiz';
    
    return `# ASSESSMENT SHEETS
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Assessment Type:** ${assessmentType}
**Duration:** ${data.duration_minutes || 30} minutes
**Date:** ${new Date().toLocaleDateString()}

## STUDENT INFORMATION
**Name:** _________________________
**Class:** _________________________
**Date:** _________________________

## INSTRUCTIONS
• Read each question carefully
• Show all your work
• Use the space provided for your answers
• Ask for help if you need clarification
• Check your work before submitting

## ASSESSMENT QUESTIONS

### Section A: Basic Concepts (${gradeLevel === 1 ? '5' : '4'} points each)
**1.** What is addition?
   Answer: _________________________________

**2.** What is subtraction?
   Answer: _________________________________

**3.** ${gradeLevel === 1 ? 'Count the objects and write the number:' : 'Identify the operation needed:'}
   ${gradeLevel === 1 ? '🍎🍎🍎 + 🍎🍎 = _____' : 'Sarah has 15 stickers. She gives away 7. How many does she have left?'}
   Answer: _________________________________

**4.** ${gradeLevel === 1 ? 'Complete the pattern: 2, 4, 6, ___, 10' : 'Solve: 8 + 5 = _____'}
   Answer: _________________________________

${gradeLevel > 1 ? `**5.** What is the difference between addition and subtraction?
   Answer: _________________________________` : ''}

### Section B: Problem Solving (${gradeLevel === 1 ? '6' : '8'} points each)
**${gradeLevel === 1 ? '5' : '6'}.** ${gradeLevel === 1 ? 'Tom has 3 apples. He buys 4 more. How many apples does he have in total?' : 'A store has 25 books. They sell 12 books in the morning and 8 books in the afternoon. How many books are left?'}
   
   Show your work:
   _________________________________
   _________________________________
   
   Answer: _________________________

**${gradeLevel === 1 ? '6' : '7'}.** ${gradeLevel === 1 ? 'Lisa has 8 stickers. She gives 3 to her friend. How many stickers does she have left?' : 'Create a word problem using the numbers 15 and 7. Then solve it.'}
   
   Show your work:
   _________________________________
   _________________________________
   
   Answer: _________________________

### Section C: Application (${gradeLevel === 1 ? '8' : '10'} points each)
**${gradeLevel === 1 ? '7' : '8'}.** ${gradeLevel === 1 ? 'Draw a picture to show 5 + 3 = 8' : 'Explain your thinking: Why is it important to check your work in math?'}
   
   ${gradeLevel === 1 ? '[Space for drawing]' : 'Answer: _________________________________\n_________________________________\n_________________________________'}
   
   ${gradeLevel === 1 ? 'Answer: 5 + 3 = 8' : ''}

**${gradeLevel === 1 ? '8' : '9'}.** ${gradeLevel === 1 ? 'Write a number sentence for this story: "There are 6 birds on a tree. 2 birds fly away. How many birds are left?"' : 'Solve this multi-step problem: Maria has 20 stickers. She gives 5 to her sister and 3 to her brother. Then she buys 8 more stickers. How many stickers does she have now?'}
   
   ${gradeLevel === 1 ? 'Number sentence: _________________' : 'Show your work:\n_________________________________\n_________________________________\n_________________________________\n\nAnswer: _________________________'}

### Section D: Extension (Bonus - 5 points)
**${gradeLevel === 1 ? '9' : '10'}.** ${gradeLevel === 1 ? 'Find two numbers that add up to 10. Write as many different combinations as you can.' : 'Create a story problem that uses both addition and subtraction. Solve it and explain your solution.'}
   
   ${gradeLevel === 1 ? 'Combinations:\n_____ + _____ = 10\n_____ + _____ = 10\n_____ + _____ = 10' : 'Story problem:\n_________________________________\n_________________________________\n\nSolution:\n_________________________________\n_________________________________\n\nAnswer: _________________________'}

## SELF-ASSESSMENT
**How do you feel about this assessment?**
□ I understood everything
□ I understood most of it
□ I understood some of it
□ I need more help

**What was the most challenging part?**
_________________________________

**What was the easiest part?**
_________________________________

---
*Assessment created by A2A School KPI System - Assessment Agent*
    `.trim();
  };

  const generateGradingRubric = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    
    return `# COMPREHENSIVE GRADING RUBRIC
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Assessment Type:** ${data.assessment_type || 'quiz'}

## OVERALL SCORING SCALE
**A (90-100%):** Exceeds Expectations - Demonstrates mastery with advanced understanding
**B (80-89%):** Meets Expectations - Demonstrates solid understanding of concepts
**C (70-79%):** Approaching Expectations - Shows basic understanding with some gaps
**D (60-69%):** Below Expectations - Shows limited understanding, needs support
**F (Below 60%):** Well Below Expectations - Shows minimal understanding, needs significant support

## DETAILED CRITERIA

### Section A: Basic Concepts (${gradeLevel === 1 ? '20' : '16'} points total)
**Excellent (4 points per question):**
• Provides complete, accurate definitions
• Demonstrates clear understanding of concepts
• Uses appropriate mathematical language
• Shows confidence in responses

**Good (3 points per question):**
• Provides mostly accurate definitions
• Shows good understanding with minor gaps
• Uses some mathematical language
• Shows reasonable confidence

**Satisfactory (2 points per question):**
• Provides basic definitions with some inaccuracies
• Shows partial understanding
• Limited use of mathematical language
• Shows some uncertainty

**Needs Improvement (1 point per question):**
• Provides incomplete or incorrect definitions
• Shows limited understanding
• Little to no mathematical language
• Shows significant uncertainty

### Section B: Problem Solving (${gradeLevel === 1 ? '12' : '16'} points total)
**Excellent (${gradeLevel === 1 ? '6' : '8'} points per question):**
• Correctly identifies the problem type
• Shows clear, logical solution process
• Uses appropriate strategies and methods
• Provides accurate final answer
• Shows work clearly and completely

**Good (${gradeLevel === 1 ? '5' : '6-7'} points per question):**
• Mostly correctly identifies problem type
• Shows mostly logical solution process
• Uses appropriate strategies with minor errors
• Provides mostly accurate answer
• Shows work with minor gaps

**Satisfactory (${gradeLevel === 1 ? '3-4' : '4-5'} points per question):**
• Partially identifies problem type
• Shows some logical steps but with errors
• Uses some appropriate strategies
• Provides partially correct answer
• Shows some work but incomplete

**Needs Improvement (${gradeLevel === 1 ? '1-2' : '1-3'} points per question):**
• Incorrectly identifies problem type
• Shows unclear or illogical process
• Uses inappropriate strategies
• Provides incorrect answer
• Shows little to no work

### Section C: Application (${gradeLevel === 1 ? '16' : '20'} points total)
**Excellent (${gradeLevel === 1 ? '8' : '10'} points per question):**
• Demonstrates deep understanding of concepts
• Applies knowledge to new situations effectively
• Shows creative and original thinking
• Provides clear explanations and reasoning
• Makes connections to real-world applications

**Good (${gradeLevel === 1 ? '6-7' : '8-9'} points per question):**
• Demonstrates solid understanding
• Applies knowledge with minor limitations
• Shows some creative thinking
• Provides mostly clear explanations
• Makes some real-world connections

**Satisfactory (${gradeLevel === 1 ? '4-5' : '5-7'} points per question):**
• Demonstrates basic understanding
• Limited application to new situations
• Shows minimal creative thinking
• Provides basic explanations
• Makes few real-world connections

**Needs Improvement (${gradeLevel === 1 ? '1-3' : '1-4'} points per question):**
• Demonstrates limited understanding
• Cannot apply knowledge to new situations
• Shows no creative thinking
• Provides unclear explanations
• Makes no real-world connections

### Section D: Extension (Bonus - 5 points)
**Full Credit (5 points):**
• Demonstrates advanced understanding
• Shows exceptional problem-solving skills
• Provides creative and original solutions
• Makes sophisticated connections

**Partial Credit (2-4 points):**
• Shows good understanding with some limitations
• Demonstrates solid problem-solving skills
• Provides mostly creative solutions
• Makes some connections

**No Credit (0-1 points):**
• Shows limited understanding
• Demonstrates basic problem-solving skills
• Provides simple or incomplete solutions
• Makes few or no connections

## ADDITIONAL ASSESSMENT CRITERIA

### Work Quality
**Excellent:**
• Work is neat, organized, and easy to follow
• Shows clear thinking process
• Uses appropriate mathematical notation
• Demonstrates attention to detail

**Good:**
• Work is mostly neat and organized
• Shows mostly clear thinking
• Uses mostly appropriate notation
• Shows good attention to detail

**Satisfactory:**
• Work is somewhat neat and organized
• Shows some clear thinking
• Uses some appropriate notation
• Shows basic attention to detail

**Needs Improvement:**
• Work is messy or disorganized
• Shows unclear thinking
• Uses inappropriate notation
• Shows poor attention to detail

### Effort and Persistence
**Excellent:**
• Shows consistent effort throughout
• Persists through challenging problems
• Asks thoughtful questions
• Demonstrates engagement and interest

**Good:**
• Shows good effort most of the time
• Persists through most problems
• Asks some questions
• Shows reasonable engagement

**Satisfactory:**
• Shows basic effort
• Persists through some problems
• Asks few questions
• Shows limited engagement

**Needs Improvement:**
• Shows minimal effort
• Gives up easily on problems
• Asks no questions
• Shows little engagement

## FEEDBACK GUIDELINES

### Positive Feedback Examples
• "Excellent work showing your thinking process!"
• "Great job using multiple strategies to solve this problem!"
• "I can see you really understand the concept!"
• "Your explanation is very clear and detailed!"

### Constructive Feedback Examples
• "Try showing one more step in your work"
• "Remember to check your answer by working backwards"
• "Consider using a different strategy for this type of problem"
• "Your answer is close - double-check your calculation"

### Next Steps Suggestions
• "Practice more problems like this at home"
• "Review the concept of [specific topic]"
• "Try the extension problems for extra challenge"
• "Work with a partner to discuss different solution methods"

---
*Rubric created by A2A School KPI System - Assessment Agent*
    `.trim();
  };

  const generateAnswerKey = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    
    return `# COMPREHENSIVE ANSWER KEY
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Assessment Type:** ${data.assessment_type || 'quiz'}

## SECTION A: BASIC CONCEPTS

**1.** What is addition?
   **Answer:** Addition is combining two or more numbers to find the total or sum.

**2.** What is subtraction?
   **Answer:** Subtraction is taking away one number from another to find the difference.

**3.** ${gradeLevel === 1 ? 'Count the objects and write the number:' : 'Identify the operation needed:'}
   ${gradeLevel === 1 ? '🍎🍎🍎 + 🍎🍎 = _____' : 'Sarah has 15 stickers. She gives away 7. How many does she have left?'}
   **Answer:** ${gradeLevel === 1 ? '5' : 'Subtraction; 15 - 7 = 8 stickers'}

**4.** ${gradeLevel === 1 ? 'Complete the pattern: 2, 4, 6, ___, 10' : 'Solve: 8 + 5 = _____'}
   **Answer:** ${gradeLevel === 1 ? '8' : '13'}

${gradeLevel > 1 ? `**5.** What is the difference between addition and subtraction?
   **Answer:** Addition combines numbers to find a sum, while subtraction takes away numbers to find a difference.` : ''}

## SECTION B: PROBLEM SOLVING

**${gradeLevel === 1 ? '5' : '6'}.** ${gradeLevel === 1 ? 'Tom has 3 apples. He buys 4 more. How many apples does he have in total?' : 'A store has 25 books. They sell 12 books in the morning and 8 books in the afternoon. How many books are left?'}
   
   **Work:** ${gradeLevel === 1 ? '3 + 4 = 7' : '25 - 12 - 8 = 13 - 8 = 5'}
   **Answer:** ${gradeLevel === 1 ? '7 apples' : '5 books'}

**${gradeLevel === 1 ? '6' : '7'}.** ${gradeLevel === 1 ? 'Lisa has 8 stickers. She gives 3 to her friend. How many stickers does she have left?' : 'Create a word problem using the numbers 15 and 7. Then solve it.'}
   
   **Work:** ${gradeLevel === 1 ? '8 - 3 = 5' : 'Sample: "John has 15 marbles. He loses 7 marbles. How many marbles does he have left? 15 - 7 = 8"'}
   **Answer:** ${gradeLevel === 1 ? '5 stickers' : '8 marbles (answers will vary for word problems)'}

## SECTION C: APPLICATION

**${gradeLevel === 1 ? '7' : '8'}.** ${gradeLevel === 1 ? 'Draw a picture to show 5 + 3 = 8' : 'Explain your thinking: Why is it important to check your work in math?'}
   
   **Answer:** ${gradeLevel === 1 ? 'Students should draw 5 objects + 3 objects = 8 total objects' : 'Checking work helps catch mistakes, ensures accuracy, and builds confidence in mathematical reasoning.'}

**${gradeLevel === 1 ? '8' : '9'}.** ${gradeLevel === 1 ? 'Write a number sentence for this story: "There are 6 birds on a tree. 2 birds fly away. How many birds are left?"' : 'Solve this multi-step problem: Maria has 20 stickers. She gives 5 to her sister and 3 to her brother. Then she buys 8 more stickers. How many stickers does she have now?'}
   
   **Answer:** ${gradeLevel === 1 ? '6 - 2 = 4' : '20 - 5 - 3 + 8 = 12 - 3 + 8 = 9 + 8 = 17 stickers'}

## SECTION D: EXTENSION (BONUS)

**${gradeLevel === 1 ? '9' : '10'}.** ${gradeLevel === 1 ? 'Find two numbers that add up to 10. Write as many different combinations as you can.' : 'Create a story problem that uses both addition and subtraction. Solve it and explain your solution.'}
   
   **Answer:** ${gradeLevel === 1 ? 'Possible combinations: 1+9, 2+8, 3+7, 4+6, 5+5, 6+4, 7+3, 8+2, 9+1' : 'Answers will vary. Sample: "Tom had 20 cookies. He ate 5 cookies and then baked 8 more. How many cookies does he have now? 20 - 5 + 8 = 23 cookies"'}

## SCORING GUIDE

### Point Values
**Section A:** ${gradeLevel === 1 ? '20' : '16'} points total (${gradeLevel === 1 ? '5' : '4'} points per question)
**Section B:** ${gradeLevel === 1 ? '12' : '16'} points total (${gradeLevel === 1 ? '6' : '8'} points per question)
**Section C:** ${gradeLevel === 1 ? '16' : '20'} points total (${gradeLevel === 1 ? '8' : '10'} points per question)
**Section D:** 5 points (bonus)

**Total Possible Points:** ${gradeLevel === 1 ? '53' : '57'} points (including bonus)

### Grade Conversion
**A (90-100%):** ${gradeLevel === 1 ? '48-53' : '51-57'} points
**B (80-89%):** ${gradeLevel === 1 ? '42-47' : '46-50'} points
**C (70-79%):** ${gradeLevel === 1 ? '37-41' : '40-45'} points
**D (60-69%):** ${gradeLevel === 1 ? '32-36' : '34-39'} points
**F (Below 60%):** ${gradeLevel === 1 ? 'Below 32' : 'Below 34'} points

## COMMON MISTAKES TO WATCH FOR

### Conceptual Errors
• Confusing addition and subtraction operations
• Misunderstanding place value concepts
• Incorrect use of mathematical symbols
• Difficulty with word problem interpretation

### Computational Errors
• Basic addition/subtraction mistakes
• Borrowing errors in subtraction
• Carrying errors in addition
• Order of operations mistakes

### Process Errors
• Not showing work clearly
• Skipping steps in multi-step problems
• Not checking answers
• Misreading questions or instructions

## FEEDBACK SUGGESTIONS

### For High Performers
• "Excellent work! Try the extension problems for extra challenge."
• "Great problem-solving strategies! Consider explaining your thinking to help others."
• "Outstanding accuracy! You're ready for more complex problems."

### For Average Performers
• "Good work overall! Focus on showing all your steps clearly."
• "You understand the concepts well. Practice checking your calculations."
• "Nice effort! Review the problems you missed and try similar ones."

### For Struggling Students
• "Let's work together on the concepts you found challenging."
• "Good effort! We'll practice more problems like these."
• "I can see you're trying hard. Let's break down the problems into smaller steps."

---
*Answer Key created by A2A School KPI System - Assessment Agent*
    `.trim();
  };

  const generateAdminChecklist = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    const duration = data.duration_minutes || 30;
    const classSize = data.student_count || 25;
    
    return `# COMPREHENSIVE ASSESSMENT ADMINISTRATION CHECKLIST
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Duration:** ${duration} minutes
**Class Size:** ${classSize} students
**Date:** ${new Date().toLocaleDateString()}

## PRE-ASSESSMENT PREPARATION (15 minutes before)

### Materials & Resources
- [ ] Assessment sheets printed and organized
- [ ] Answer keys prepared and secured
- [ ] Extra pencils and erasers available
- [ ] Scratch paper provided
- [ ] Timer/clock visible to all students
- [ ] Backup assessment copies ready
- [ ] Technology tested (if applicable)

### Classroom Environment
- [ ] Desks arranged for optimal focus
- [ ] Distractions removed or minimized
- [ ] Good lighting throughout room
- [ ] Temperature comfortable
- [ ] Noise level appropriate
- [ ] Emergency procedures reviewed
- [ ] Restroom policy communicated

### Student Preparation
- [ ] Assessment instructions reviewed
- [ ] Time allocation clearly stated
- [ ] Question format explained
- [ ] Accommodation needs addressed
- [ ] Student questions answered
- [ ] Materials distributed
- [ ] Seating arrangements finalized

### Teacher Preparation
- [ ] Assessment objectives reviewed
- [ ] Grading criteria prepared
- [ ] Monitoring strategy planned
- [ ] Support procedures established
- [ ] Time management plan ready
- [ ] Backup activities prepared
- [ ] Communication plan with support staff

## DURING ASSESSMENT MONITORING

### Active Monitoring (Every 5 minutes)
- [ ] Circulate around classroom
- [ ] Check student progress
- [ ] Answer clarification questions
- [ ] Monitor time remaining
- [ ] Observe student behavior
- [ ] Provide encouragement as needed
- [ ] Address any technical issues

### Time Management
- [ ] Announce time milestones (${Math.floor(duration * 0.5)} minutes remaining)
- [ ] Give 10-minute warning
- [ ] Give 5-minute warning
- [ ] Give 2-minute warning
- [ ] Announce final minute
- [ ] Ensure smooth conclusion

### Student Support
- [ ] Answer procedural questions only
- [ ] Provide clarification when needed
- [ ] Offer encouragement to struggling students
- [ ] Monitor for signs of stress or anxiety
- [ ] Ensure all students are working
- [ ] Address any behavioral issues
- [ ] Maintain positive atmosphere

### Accommodation Monitoring
- [ ] Check extended time students
- [ ] Monitor read-aloud assistance
- [ ] Ensure breaks are provided as needed
- [ ] Verify alternative formats are working
- [ ] Support students with special needs
- [ ] Document any accommodations used
- [ ] Maintain equity and fairness

## POST-ASSESSMENT PROCEDURES

### Collection & Organization
- [ ] Collect all assessment materials
- [ ] Verify all students submitted work
- [ ] Organize papers by class period
- [ ] Secure answer keys and materials
- [ ] Clean up classroom environment
- [ ] Return borrowed materials
- [ ] Document any incidents or issues

### Immediate Follow-up
- [ ] Thank students for their effort
- [ ] Provide general feedback on process
- [ ] Answer any immediate questions
- [ ] Announce when results will be available
- [ ] Transition to next activity
- [ ] Address any student concerns
- [ ] Maintain positive classroom climate

### Documentation & Planning
- [ ] Record completion rates
- [ ] Note any technical difficulties
- [ ] Document accommodation usage
- [ ] Plan grading timeline
- [ ] Schedule result review
- [ ] Prepare for next lesson
- [ ] Update student records

## QUALITY ASSURANCE CHECKLIST

### Assessment Integrity
- [ ] All students had equal opportunity
- [ ] Instructions were clear and consistent
- [ ] Time allocation was appropriate
- [ ] Materials were in good condition
- [ ] Environment was conducive to success
- [ ] Accommodations were properly provided
- [ ] No unfair advantages or disadvantages

### Data Collection
- [ ] Student responses collected completely
- [ ] Attendance recorded accurately
- [ ] Time data documented
- [ ] Behavioral observations noted
- [ ] Accommodation usage tracked
- [ ] Technical issues recorded
- [ ] Teacher notes completed

## EMERGENCY PROCEDURES

### Technical Issues
- [ ] Backup assessment ready
- [ ] Alternative activities prepared
- [ ] Technology support contact available
- [ ] Manual procedures established
- [ ] Communication plan with administration
- [ ] Student notification process ready

### Behavioral Issues
- [ ] School behavior policy reviewed
- [ ] Support staff contact information ready
- [ ] Alternative supervision arranged
- [ ] Documentation procedures established
- [ ] Parent communication plan ready
- [ ] Follow-up procedures planned

### Health & Safety
- [ ] First aid procedures reviewed
- [ ] Emergency contact information available
- [ ] Evacuation procedures understood
- [ ] Medical accommodation needs addressed
- [ ] Support staff notified of special needs
- [ ] Emergency communication plan ready

---
*Checklist created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateClassroomSetup = (data) => {
    const gradeLevel = data.grade_level || 1;
    const classSize = data.student_count || 25;
    const assessmentType = data.assessment_type || 'quiz';
    
    return `# OPTIMAL CLASSROOM SETUP GUIDE
**Grade Level:** ${gradeLevel}
**Class Size:** ${classSize} students
**Assessment Type:** ${assessmentType}
**Subject:** ${data.subject || 'Mathematics'}

## PHYSICAL ENVIRONMENT OPTIMIZATION

### Seating Arrangements
**For Individual Assessment:**
• Traditional rows facing the front
• Adequate space between students (minimum 3 feet)
• Clear view of instructional materials
• Easy access to supplies and resources
• Consideration for left-handed students
• Optimal distance from windows and doors
• Clear pathways for teacher movement

**For Group Assessment (if applicable):**
• Clusters of 4-6 students maximum
• Clear sight lines to all students
• Adequate space for materials
• Minimal cross-talk opportunities
• Easy monitoring and support access

### Lighting & Visibility
• Natural light from windows (not directly in eyes)
• Overhead lighting evenly distributed
• No shadows on student work areas
• Clear visibility of clock/timer
• Good contrast for reading materials
• Emergency lighting accessible
• Adjustable lighting for different needs

### Noise & Distraction Management
• Minimize external noise sources
• Close windows facing busy areas
• Turn off unnecessary electronics
• Remove or cover distracting displays
• Establish quiet signal system
• Plan for unexpected interruptions
• Create "do not disturb" environment

## MATERIALS & RESOURCES SETUP

### Assessment Materials
• Assessment sheets organized by class
• Answer keys secured and accessible
• Extra pencils, erasers, and sharpeners
• Scratch paper for calculations
• Rulers, calculators (if permitted)
• Backup copies of all materials
• Clear labeling and organization

### Technology Setup (if applicable)
• Computers/tablets tested and ready
• Internet connection verified
• Software applications loaded
• Backup devices available
• Technical support contact ready
• Manual procedures as backup
• Student login information prepared

### Support Materials
• Reference materials (if permitted)
• Visual aids and charts
• Manipulatives (if applicable)
• Emergency supplies (tissues, etc.)
• First aid kit accessible
• Communication devices ready
• Backup activities prepared

## STUDENT ACCOMMODATION SETUP

### Special Needs Considerations
• Wheelchair accessible seating
• Visual impairment accommodations
• Hearing impairment support
• Attention management strategies
• Extended time arrangements
• Alternative format materials
• Support staff positioning

### Individual Student Needs
• Review IEP/504 accommodations
• Prepare specialized materials
• Arrange for support personnel
• Set up assistive technology
• Plan for breaks and movement
• Consider sensory needs
• Ensure equity and fairness

## MONITORING & SUPERVISION SETUP

### Teacher Positioning
• Central location for optimal visibility
• Easy access to all students
• Clear view of entrance/exit
• Proximity to emergency equipment
• Ability to monitor time effectively
• Access to support materials
• Comfortable for extended monitoring

### Support Staff Arrangements
• Clear roles and responsibilities
• Communication protocols established
• Backup supervision planned
• Special needs support positioned
• Emergency response procedures
• Documentation responsibilities
• Coordination with main teacher

## ENVIRONMENTAL CONTROLS

### Temperature & Air Quality
• Comfortable temperature (68-72°F)
• Good air circulation
• No strong odors or allergens
• Humidity at comfortable levels
• Emergency ventilation available
• Climate control accessible
• Student comfort prioritized

### Safety & Security
• Clear emergency exits
• First aid supplies accessible
• Emergency contact information posted
• Security procedures understood
• Visitor protocols established
• Lockdown procedures reviewed
• Communication systems ready

## PRE-ASSESSMENT FINAL CHECKS

### 5 Minutes Before Start
- [ ] All materials distributed
- [ ] Technology tested and working
- [ ] Seating arrangements finalized
- [ ] Instructions prepared and clear
- [ ] Timer/clock visible and set
- [ ] Support staff in position
- [ ] Emergency procedures reviewed

### 1 Minute Before Start
- [ ] Final student count confirmed
- [ ] All students seated and ready
- [ ] Materials in place
- [ ] Instructions ready to deliver
- [ ] Timer started
- [ ] Monitoring position assumed
- [ ] Positive atmosphere established

## POST-ASSESSMENT CLEANUP

### Immediate Cleanup
• Collect all assessment materials
• Organize papers and supplies
• Return borrowed materials
• Reset classroom arrangement
• Clean up any messes
• Secure valuable items
• Prepare for next activity

### Environment Reset
• Restore normal seating
• Adjust lighting as needed
• Remove assessment-specific materials
• Reset technology settings
• Clean and organize workspace
• Prepare for regular instruction
• Maintain positive atmosphere

---
*Setup Guide created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateAccommodationGuide = (data) => {
    const gradeLevel = data.grade_level || 1;
    const classSize = data.student_count || 25;
    
    return `# COMPREHENSIVE ACCOMMODATION GUIDELINES
**Grade Level:** ${gradeLevel}
**Class Size:** ${classSize} students
**Subject:** ${data.subject || 'Mathematics'}
**Assessment Type:** ${data.assessment_type || 'quiz'}

## LEGAL FRAMEWORK & COMPLIANCE

### Federal Requirements
• **IDEA (Individuals with Disabilities Education Act)**
• **Section 504 of the Rehabilitation Act**
• **Americans with Disabilities Act (ADA)**
• **FERPA (Family Educational Rights and Privacy Act)**
• **State and local accommodation policies**
• **School district procedures**
• **Individual Education Plans (IEPs)**

### Documentation Requirements
• Current IEP or 504 plan on file
• Accommodation needs clearly specified
• Parent/guardian consent documented
• Teacher training records maintained
• Implementation logs completed
• Progress monitoring data collected
• Regular review and updates scheduled

## COMMON ACCOMMODATION CATEGORIES

### Extended Time Accommodations
**Standard Extended Time (1.5x):**
• 45 minutes for 30-minute assessment
• 60 minutes for 40-minute assessment
• 90 minutes for 60-minute assessment
• Quiet, distraction-free environment
• Breaks allowed as needed
• Same assessment content and format

**Double Time (2x):**
• 60 minutes for 30-minute assessment
• 80 minutes for 40-minute assessment
• 120 minutes for 60-minute assessment
• Separate testing location recommended
• Frequent breaks encouraged
• Additional support available

### Reading & Language Accommodations
**Read-Aloud Assistance:**
• Teacher or trained aide reads questions
• Student can request re-reading
• No interpretation or explanation provided
• Clear, consistent reading pace
• Student controls reading speed
• Alternative formats available

**Large Print Materials:**
• 18-point font minimum
• High contrast printing
• Clear, simple formatting
• Adequate spacing between items
• No visual clutter or distractions
• Consistent with original format

**Audio Recordings:**
• Professional quality recordings
• Clear pronunciation and pacing
• Student controls playback speed
• Headphones provided for privacy
• Backup written materials available
• Technical support ready

### Writing & Response Accommodations
**Scribe Services:**
• Trained adult writes student responses
• Student dictates exact answers
• No interpretation or editing
• Student reviews and approves responses
• Clear documentation of process
• Alternative response methods available

**Word Processing:**
• Computer with word processing software
• Spell-check and grammar tools available
• Student familiar with technology
• Backup manual methods ready
• Technical support available
• Same assessment content maintained

**Alternative Response Formats:**
• Oral responses recorded
• Pointing or gesturing to answers
• Drawing or diagramming responses
• Manipulative-based responses
• Technology-assisted responses
• Multiple format options available

### Environmental Accommodations
**Separate Testing Location:**
• Quiet, distraction-free environment
• Individual or small group setting
• Consistent, comfortable temperature
• Good lighting and ventilation
• Minimal visual distractions
• Emergency procedures accessible

**Preferential Seating:**
• Near teacher for support
• Away from distractions
• Good lighting and visibility
• Comfortable seating options
• Access to materials and resources
• Easy exit for breaks

**Sensory Accommodations:**
• Noise-canceling headphones
• Fidget tools or stress balls
• Weighted blankets or lap pads
• Visual schedules or timers
• Calming music or white noise
• Sensory breaks as needed

## IMPLEMENTATION STRATEGIES

### Pre-Assessment Planning
• Review individual student needs
• Prepare specialized materials
• Arrange for support personnel
• Set up appropriate environment
• Communicate with all stakeholders
• Practice accommodation procedures
• Prepare backup plans

### During Assessment Support
• Monitor accommodation effectiveness
• Provide support as specified
• Document any issues or concerns
• Maintain assessment integrity
• Ensure student comfort and success
• Communicate with support staff
• Adjust as needed within guidelines

### Post-Assessment Follow-up
• Document accommodation usage
• Evaluate effectiveness
• Gather student feedback
• Update accommodation plans
• Share results with team
• Plan for future assessments
• Maintain confidentiality

## SPECIFIC DISABILITY ACCOMMODATIONS

### Learning Disabilities
• Extended time for processing
• Read-aloud for reading difficulties
• Calculator for math calculations
• Graphic organizers for organization
• Memory aids and reference sheets
• Alternative response formats
• Frequent breaks for attention

### Attention Deficit/Hyperactivity Disorder (ADHD)
• Extended time for focus
• Frequent breaks for movement
• Fidget tools for concentration
• Separate, quiet environment
• Clear, simple instructions
• Visual timers and schedules
• Positive reinforcement strategies

### Autism Spectrum Disorder (ASD)
• Predictable, structured environment
• Visual schedules and instructions
• Sensory accommodations
• Extended time for processing
• Clear, literal language
• Consistent routines and procedures
• Support for social interactions

### Physical Disabilities
• Accessible seating and materials
• Assistive technology devices
• Scribe or voice recognition software
• Extended time for physical tasks
• Alternative response methods
• Accessible testing environment
• Support personnel as needed

### Visual Impairments
• Large print or Braille materials
• Audio recordings of content
• Magnification devices
• High contrast materials
• Extended time for reading
• Scribe for written responses
• Accessible technology tools

### Hearing Impairments
• Sign language interpreters
• Written instructions and questions
• Visual cues and demonstrations
• Extended time for processing
• Assistive listening devices
• Captioning for audio content
• Visual communication methods

## MONITORING & EVALUATION

### Effectiveness Assessment
• Student performance data
• Student feedback and preferences
• Teacher observations
• Parent/guardian input
• Support staff reports
• Progress monitoring results
• Accommodation usage logs

### Continuous Improvement
• Regular review of accommodations
• Updates based on student needs
• Training for all staff members
• Communication with families
• Collaboration with specialists
• Documentation of changes
• Evaluation of outcomes

## DOCUMENTATION REQUIREMENTS

### Required Records
• Individual accommodation plans
• Implementation logs
• Student performance data
• Teacher observations
• Parent/guardian communications
• Support staff reports
• Progress monitoring results

### Confidentiality Protocols
• Secure storage of records
• Limited access to information
• Parent/guardian consent
• Student privacy protection
• Staff training on confidentiality
• Regular review of access
• Compliance with FERPA

---
*Accommodation Guide created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateGradingReport = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    const classSize = data.student_count || 25;
    const classAverage = Math.floor(Math.random() * 20) + 75;
    const highestScore = Math.floor(Math.random() * 10) + 90;
    const lowestScore = Math.floor(Math.random() * 20) + 60;
    
    return `# COMPREHENSIVE GRADING REPORT
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Class Size:** ${classSize} students
**Assessment Date:** ${new Date().toLocaleDateString()}
**Grading Date:** ${new Date().toLocaleDateString()}

## OVERALL CLASS PERFORMANCE

### Summary Statistics
**Class Average:** ${classAverage}%
**Highest Score:** ${highestScore}%
**Lowest Score:** ${lowestScore}%
**Standard Deviation:** ${(Math.random() * 10 + 8).toFixed(1)}
**Median Score:** ${Math.floor((highestScore + lowestScore) / 2)}%
**Range:** ${highestScore - lowestScore} points

### Grade Distribution
**A Grade (90-100%):** ${Math.floor(Math.random() * 8 + 5)} students (${Math.floor((Math.random() * 8 + 5) / classSize * 100)}%)
**B Grade (80-89%):** ${Math.floor(Math.random() * 10 + 8)} students (${Math.floor((Math.random() * 10 + 8) / classSize * 100)}%)
**C Grade (70-79%):** ${Math.floor(Math.random() * 8 + 6)} students (${Math.floor((Math.random() * 8 + 6) / classSize * 100)}%)
**D Grade (60-69%):** ${Math.floor(Math.random() * 4 + 2)} students (${Math.floor((Math.random() * 4 + 2) / classSize * 100)}%)
**F Grade (Below 60%):** ${Math.floor(Math.random() * 3 + 1)} students (${Math.floor((Math.random() * 3 + 1) / classSize * 100)}%)

## DETAILED STUDENT PERFORMANCE

### High Performers (90% and above)
${Array.from({length: Math.floor(Math.random() * 5 + 3)}, (_, i) => 
  `**Student ${i + 1}:** ${Math.floor(Math.random() * 10 + 90)}% - Excellent understanding of concepts, strong problem-solving skills`
).join('\n')}

### Proficient Students (80-89%)
${Array.from({length: Math.floor(Math.random() * 8 + 5)}, (_, i) => 
  `**Student ${i + 1}:** ${Math.floor(Math.random() * 10 + 80)}% - Good grasp of material, minor areas for improvement`
).join('\n')}

### Developing Students (70-79%)
${Array.from({length: Math.floor(Math.random() * 6 + 4)}, (_, i) => 
  `**Student ${i + 1}:** ${Math.floor(Math.random() * 10 + 70)}% - Basic understanding, needs additional practice`
).join('\n')}

### Students Needing Support (Below 70%)
${Array.from({length: Math.floor(Math.random() * 4 + 2)}, (_, i) => 
  `**Student ${i + 1}:** ${Math.floor(Math.random() * 10 + 60)}% - Requires targeted intervention and support`
).join('\n')}

## SECTION-BY-SECTION ANALYSIS

### Section A: Basic Concepts (${gradeLevel === 1 ? '20' : '16'} points)
**Class Average:** ${Math.floor(Math.random() * 15 + 80)}%
**Common Strengths:**
• Understanding of fundamental operations
• Ability to identify key concepts
• Basic problem recognition

**Common Challenges:**
• Confusion between addition and subtraction
• Difficulty with word problem interpretation
• Inconsistent application of concepts

### Section B: Problem Solving (${gradeLevel === 1 ? '12' : '16'} points)
**Class Average:** ${Math.floor(Math.random() * 20 + 70)}%
**Common Strengths:**
• Logical thinking processes
• Step-by-step problem solving
• Persistence in working through problems

**Common Challenges:**
• Multi-step problem complexity
• Calculation errors
• Time management under pressure

### Section C: Application (${gradeLevel === 1 ? '16' : '20'} points)
**Class Average:** ${Math.floor(Math.random() * 18 + 75)}%
**Common Strengths:**
• Real-world connections
• Creative problem-solving approaches
• Clear explanations of thinking

**Common Challenges:**
• Transferring knowledge to new situations
• Explaining reasoning clearly
• Making connections to daily life

### Section D: Extension (Bonus - 5 points)
**Participation Rate:** ${Math.floor(Math.random() * 30 + 60)}%
**Average Score:** ${(Math.random() * 2 + 3).toFixed(1)}/5 points
**Notable Achievements:**
• Creative problem-solving approaches
• Advanced mathematical thinking
• Excellent communication of ideas

## INDIVIDUAL STUDENT FEEDBACK

### Exceptional Performance
${Array.from({length: 2}, (_, i) => 
  `**Student ${i + 1}:** Outstanding work! Your understanding of ${topic} is excellent. You demonstrated advanced problem-solving skills and clear communication. Consider challenging yourself with extension activities.`
).join('\n\n')}

### Strong Performance
${Array.from({length: 3}, (_, i) => 
  `**Student ${i + 1}:** Great job! You show solid understanding of the concepts. Continue practicing to build confidence and speed. Your problem-solving approach is logical and well-organized.`
).join('\n\n')}

### Areas for Growth
${Array.from({length: 3}, (_, i) => 
  `**Student ${i + 1}:** Good effort! Focus on [specific area] to improve your understanding. Practice [specific skill] regularly. I'm confident you can achieve your goals with continued effort.`
).join('\n\n')}

### Need Additional Support
${Array.from({length: 2}, (_, i) => 
  `**Student ${i + 1}:** I see you're working hard. Let's focus on building a strong foundation in [specific concept]. We'll work together to help you succeed.`
).join('\n\n')}

## RECOMMENDATIONS FOR IMPROVEMENT

### For High Performers
• Provide extension activities and challenges
• Encourage peer tutoring opportunities
• Offer advanced problem-solving tasks
• Introduce real-world applications
• Support independent research projects

### For Average Performers
• Focus on building confidence through practice
• Provide additional examples and explanations
• Encourage collaborative learning
• Offer multiple practice opportunities
• Support gradual skill development

### For Struggling Students
• Provide one-on-one or small group support
• Break down complex problems into smaller steps
• Use manipulatives and visual aids
• Offer additional practice time
• Implement targeted intervention strategies

## NEXT STEPS

### Immediate Actions (This Week)
• Review assessment results with students
• Provide individual feedback and support
• Plan targeted intervention for struggling students
• Celebrate achievements and progress
• Communicate results with parents/guardians

### Short-term Goals (Next 2 Weeks)
• Implement differentiated instruction strategies
• Provide additional practice opportunities
• Monitor progress through formative assessments
• Adjust teaching methods based on results
• Support student goal-setting and reflection

### Long-term Planning (Next Month)
• Plan review and reinforcement activities
• Prepare for next assessment cycle
• Develop ongoing progress monitoring
• Collaborate with support staff and families
• Evaluate and adjust instructional strategies

---
*Grading Report created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generatePerformanceAnalytics = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    const classSize = data.student_count || 25;
    
    return `# COMPREHENSIVE PERFORMANCE ANALYTICS
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Class Size:** ${classSize} students
**Analysis Date:** ${new Date().toLocaleDateString()}

## OVERALL PERFORMANCE DISTRIBUTION

### Grade Distribution Analysis
**A Grade (90-100%):** ${Math.floor(Math.random() * 8 + 5)} students (${Math.floor((Math.random() * 8 + 5) / classSize * 100)}%)
- **Performance Level:** Exceeds Expectations
- **Characteristics:** Advanced understanding, independent problem-solving, clear communication
- **Next Steps:** Extension activities, peer tutoring, advanced challenges

**B Grade (80-89%):** ${Math.floor(Math.random() * 10 + 8)} students (${Math.floor((Math.random() * 10 + 8) / classSize * 100)}%)
- **Performance Level:** Meets Expectations
- **Characteristics:** Solid understanding, consistent performance, good problem-solving
- **Next Steps:** Continued practice, confidence building, skill refinement

**C Grade (70-79%):** ${Math.floor(Math.random() * 8 + 6)} students (${Math.floor((Math.random() * 8 + 6) / classSize * 100)}%)
- **Performance Level:** Approaching Expectations
- **Characteristics:** Basic understanding, some gaps, needs support
- **Next Steps:** Targeted intervention, additional practice, skill building

**D Grade (60-69%):** ${Math.floor(Math.random() * 4 + 2)} students (${Math.floor((Math.random() * 4 + 2) / classSize * 100)}%)
- **Performance Level:** Below Expectations
- **Characteristics:** Limited understanding, significant gaps, needs intensive support
- **Next Steps:** Intensive intervention, one-on-one support, foundational review

**F Grade (Below 60%):** ${Math.floor(Math.random() * 3 + 1)} students (${Math.floor((Math.random() * 3 + 1) / classSize * 100)}%)
- **Performance Level:** Well Below Expectations
- **Characteristics:** Minimal understanding, major gaps, requires immediate intervention
- **Next Steps:** Immediate support, alternative assessment, intensive remediation

## STATISTICAL ANALYSIS

### Central Tendency Measures
**Mean (Average):** ${(Math.random() * 15 + 75).toFixed(1)}%
**Median:** ${Math.floor(Math.random() * 15 + 75)}%
**Mode:** ${Math.floor(Math.random() * 10 + 80)}%
**Standard Deviation:** ${(Math.random() * 8 + 6).toFixed(1)}

### Distribution Characteristics
**Skewness:** ${(Math.random() * 2 - 1).toFixed(2)} (${Math.random() > 0.5 ? 'Slightly right-skewed' : 'Slightly left-skewed'})
**Kurtosis:** ${(Math.random() * 2 - 1).toFixed(2)} (${Math.random() > 0.5 ? 'Normal distribution' : 'Slightly peaked'})
**Range:** ${Math.floor(Math.random() * 30 + 20)} points
**Interquartile Range:** ${Math.floor(Math.random() * 15 + 10)} points

## SECTION-BY-SECTION PERFORMANCE

### Section A: Basic Concepts
**Average Score:** ${Math.floor(Math.random() * 15 + 80)}%
**Standard Deviation:** ${(Math.random() * 5 + 3).toFixed(1)}
**Distribution:**
• Excellent (90-100%): ${Math.floor(Math.random() * 8 + 5)} students
• Good (80-89%): ${Math.floor(Math.random() * 10 + 8)} students
• Satisfactory (70-79%): ${Math.floor(Math.random() * 6 + 4)} students
• Needs Improvement (Below 70%): ${Math.floor(Math.random() * 4 + 2)} students

### Section B: Problem Solving
**Average Score:** ${Math.floor(Math.random() * 20 + 70)}%
**Standard Deviation:** ${(Math.random() * 6 + 4).toFixed(1)}
**Distribution:**
• Excellent (90-100%): ${Math.floor(Math.random() * 6 + 3)} students
• Good (80-89%): ${Math.floor(Math.random() * 8 + 6)} students
• Satisfactory (70-79%): ${Math.floor(Math.random() * 8 + 6)} students
• Needs Improvement (Below 70%): ${Math.floor(Math.random() * 6 + 4)} students

### Section C: Application
**Average Score:** ${Math.floor(Math.random() * 18 + 75)}%
**Standard Deviation:** ${(Math.random() * 5 + 4).toFixed(1)}
**Distribution:**
• Excellent (90-100%): ${Math.floor(Math.random() * 7 + 4)} students
• Good (80-89%): ${Math.floor(Math.random() * 9 + 7)} students
• Satisfactory (70-79%): ${Math.floor(Math.random() * 7 + 5)} students
• Needs Improvement (Below 70%): ${Math.floor(Math.random() * 4 + 2)} students

## PERFORMANCE TRENDS & PATTERNS

### Strengths Identified
• **Conceptual Understanding:** ${Math.floor(Math.random() * 20 + 70)}% of students demonstrate solid grasp of basic concepts
• **Problem-Solving Approach:** ${Math.floor(Math.random() * 15 + 75)}% show logical thinking processes
• **Communication Skills:** ${Math.floor(Math.random() * 10 + 80)}% can explain their reasoning clearly
• **Persistence:** ${Math.floor(Math.random() * 15 + 70)}% demonstrate good work habits and effort

### Areas of Concern
• **Calculation Accuracy:** ${Math.floor(Math.random() * 20 + 30)}% of students make frequent computational errors
• **Time Management:** ${Math.floor(Math.random() * 25 + 25)}% struggle with completing tasks within time limits
• **Multi-step Problems:** ${Math.floor(Math.random() * 30 + 20)}% have difficulty with complex problem-solving
• **Application Skills:** ${Math.floor(Math.random() * 25 + 15)}% struggle to apply knowledge to new situations

## COMPARATIVE ANALYSIS

### Performance by Question Type
**Multiple Choice Questions:**
• Average Score: ${Math.floor(Math.random() * 10 + 85)}%
• Common Errors: Careless mistakes, misreading questions
• Improvement Strategy: Practice with attention to detail

**Short Answer Questions:**
• Average Score: ${Math.floor(Math.random() * 15 + 75)}%
• Common Errors: Incomplete explanations, calculation errors
• Improvement Strategy: Focus on showing work clearly

**Problem-Solving Questions:**
• Average Score: ${Math.floor(Math.random() * 20 + 70)}%
• Common Errors: Skipping steps, logical errors
• Improvement Strategy: Practice step-by-step problem solving

**Application Questions:**
• Average Score: ${Math.floor(Math.random() * 18 + 72)}%
• Common Errors: Difficulty with real-world connections
• Improvement Strategy: More practice with contextual problems

## INTERVENTION RECOMMENDATIONS

### For High Performers (90%+)
• **Enrichment Activities:** Advanced problem-solving challenges
• **Leadership Opportunities:** Peer tutoring and mentoring
• **Independent Projects:** Research and presentation opportunities
• **Competition Preparation:** Math competitions and contests
• **Cross-Curricular Connections:** Integration with other subjects

### For Average Performers (70-89%)
• **Targeted Practice:** Focus on specific skill areas
• **Collaborative Learning:** Group work and peer support
• **Confidence Building:** Success-oriented activities
• **Skill Refinement:** Practice with immediate feedback
• **Goal Setting:** Individual improvement plans

### For Struggling Students (Below 70%)
• **Intensive Intervention:** Small group or one-on-one support
• **Foundational Review:** Back to basics approach
• **Alternative Strategies:** Different learning approaches
• **Frequent Assessment:** Regular progress monitoring
• **Family Involvement:** Home support strategies

## DATA-DRIVEN INSTRUCTIONAL DECISIONS

### Immediate Adjustments
• **Review and Reteach:** Focus on concepts with lowest performance
• **Differentiated Instruction:** Tailor activities to performance levels
• **Additional Practice:** Provide extra opportunities for struggling students
• **Extension Activities:** Challenge high-performing students
• **Formative Assessment:** Regular check-ins to monitor progress

### Long-term Planning
• **Curriculum Adjustments:** Modify pacing and content based on results
• **Resource Allocation:** Direct support to areas of greatest need
• **Professional Development:** Training on effective intervention strategies
• **Assessment Design:** Improve future assessments based on analysis
• **Student Support Systems:** Enhanced support for at-risk students

---
*Performance Analytics created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateGapAnalysis = (data) => {
    const topic = data.topic || 'Basic Addition and Subtraction';
    const gradeLevel = data.grade_level || 1;
    
    return `# COMPREHENSIVE LEARNING GAP ANALYSIS
**Subject:** ${data.subject || 'Mathematics'}
**Topic:** ${topic}
**Grade Level:** ${gradeLevel}
**Analysis Date:** ${new Date().toLocaleDateString()}

## EXECUTIVE SUMMARY

### Overall Assessment
Based on the comprehensive analysis of student performance, this report identifies key learning gaps and provides targeted recommendations for intervention. The analysis reveals both areas of strength and specific areas requiring immediate attention.

### Key Findings
• **${Math.floor(Math.random() * 20 + 70)}%** of students demonstrate proficiency in basic concepts
• **${Math.floor(Math.random() * 25 + 15)}%** of students require additional support in problem-solving
• **${Math.floor(Math.random() * 30 + 20)}%** of students struggle with application of knowledge
• **${Math.floor(Math.random() * 15 + 5)}%** of students need intensive intervention

## AREAS OF STRENGTH

### Conceptual Understanding
**Performance Level:** Strong (${Math.floor(Math.random() * 10 + 80)}% proficiency)
**Evidence:**
• Students demonstrate solid grasp of basic operations
• Clear understanding of mathematical vocabulary
• Ability to identify key concepts and relationships
• Strong foundation in fundamental skills

**Specific Strengths:**
• Addition and subtraction facts memorization
• Understanding of place value concepts
• Recognition of mathematical symbols and operations
• Basic problem identification skills

### Communication Skills
**Performance Level:** Good (${Math.floor(Math.random() * 15 + 75)}% proficiency)
**Evidence:**
• Students can explain their thinking processes
• Clear written communication of solutions
• Effective use of mathematical language
• Ability to justify answers and reasoning

**Specific Strengths:**
• Verbal explanation of problem-solving steps
• Written documentation of work
• Use of appropriate mathematical terminology
• Clear presentation of final answers

## AREAS NEEDING IMPROVEMENT

### Problem-Solving Strategies
**Performance Level:** Developing (${Math.floor(Math.random() * 20 + 60)}% proficiency)
**Identified Gaps:**
• Difficulty with multi-step problems
• Inconsistent use of problem-solving strategies
• Limited ability to break down complex problems
• Struggle with identifying relevant information

**Specific Challenges:**
• Word problem interpretation
• Selection of appropriate strategies
• Organization of problem-solving steps
• Persistence through challenging problems

### Application and Transfer
**Performance Level:** Needs Support (${Math.floor(Math.random() * 25 + 45)}% proficiency)
**Identified Gaps:**
• Difficulty applying knowledge to new situations
• Limited real-world connections
• Struggle with contextual problems
• Inability to transfer skills across domains

**Specific Challenges:**
• Real-world problem solving
• Making connections to daily life
• Adapting strategies for different contexts
• Creative problem-solving approaches

### Computational Accuracy
**Performance Level:** Developing (${Math.floor(Math.random() * 20 + 65)}% proficiency)
**Identified Gaps:**
• Frequent calculation errors
• Inconsistent accuracy in basic operations
• Difficulty with mental math
• Struggle with estimation skills

**Specific Challenges:**
• Basic addition and subtraction errors
• Borrowing and carrying mistakes
• Mental calculation difficulties
• Estimation and approximation skills

## DETAILED GAP ANALYSIS BY STUDENT GROUPS

### High Performers (90%+)
**Strengths:**
• Excellent conceptual understanding
• Strong problem-solving skills
• Clear communication abilities
• Independent learning capabilities

**Gaps:**
• Limited exposure to advanced challenges
• Need for more complex problem types
• Opportunity for peer leadership roles
• Potential for deeper mathematical exploration

### Average Performers (70-89%)
**Strengths:**
• Solid foundation in basic concepts
• Good work habits and persistence
• Ability to follow procedures
• Consistent effort and engagement

**Gaps:**
• Inconsistent problem-solving strategies
• Limited confidence in challenging problems
• Need for additional practice opportunities
• Difficulty with complex applications

### Struggling Students (Below 70%)
**Strengths:**
• Positive attitude and effort
• Willingness to seek help
• Basic understanding of some concepts
• Engagement in learning activities

**Gaps:**
• Fundamental skill deficiencies
• Limited problem-solving strategies
• Difficulty with abstract thinking
• Need for intensive support and intervention

## ROOT CAUSE ANALYSIS

### Instructional Factors
• **Pacing Issues:** Some students need more time to master concepts
• **Teaching Methods:** Need for varied instructional approaches
• **Practice Opportunities:** Insufficient practice with challenging problems
• **Assessment Design:** Need for more diagnostic assessments

### Student Factors
• **Prior Knowledge:** Varying levels of prerequisite skills
• **Learning Styles:** Different students need different approaches
• **Motivation:** Some students need additional engagement strategies
• **Confidence:** Self-efficacy issues affecting performance

### Environmental Factors
• **Classroom Environment:** Need for more supportive learning atmosphere
• **Resources:** Limited access to additional practice materials
• **Time Constraints:** Insufficient time for individual support
• **Support Systems:** Need for enhanced intervention programs

## TARGETED INTERVENTION RECOMMENDATIONS

### Immediate Interventions (Next 2 Weeks)

#### For Struggling Students
• **Intensive Small Group Instruction:** 3-4 students maximum
• **Foundational Skill Review:** Back to basics approach
• **One-on-One Support:** Individual attention and guidance
• **Alternative Assessment Methods:** Different ways to demonstrate learning
• **Family Involvement:** Home support strategies and resources

#### For Average Performers
• **Targeted Practice Sessions:** Focus on specific skill areas
• **Peer Tutoring:** Pair with high-performing students
• **Confidence Building Activities:** Success-oriented tasks
• **Additional Practice Materials:** Extra worksheets and activities
• **Goal Setting:** Individual improvement plans

#### For High Performers
• **Extension Activities:** Advanced problem-solving challenges
• **Independent Projects:** Research and presentation opportunities
• **Peer Leadership:** Tutoring and mentoring roles
• **Competition Preparation:** Math contests and challenges
• **Cross-Curricular Integration:** Connections to other subjects

### Medium-term Interventions (Next Month)

#### Curriculum Adjustments
• **Differentiated Instruction:** Tailor content to student needs
• **Flexible Pacing:** Allow more time for struggling students
• **Enriched Content:** Provide advanced material for high performers
• **Multiple Assessment Formats:** Various ways to demonstrate learning
• **Integrated Review:** Regular reinforcement of key concepts

#### Instructional Strategies
• **Multi-sensory Approaches:** Visual, auditory, and kinesthetic learning
• **Collaborative Learning:** Group work and peer support
• **Technology Integration:** Educational software and online resources
• **Real-world Applications:** Contextual problem-solving
• **Metacognitive Strategies:** Teaching students how to learn

### Long-term Interventions (Next Quarter)

#### System-wide Changes
• **Professional Development:** Training on effective intervention strategies
• **Resource Allocation:** Additional materials and support staff
• **Assessment Redesign:** More diagnostic and formative assessments
• **Parent Education:** Workshops on supporting student learning
• **Community Partnerships:** External resources and support

## MONITORING AND EVALUATION

### Progress Monitoring
• **Weekly Assessments:** Regular check-ins on student progress
• **Formative Assessments:** Ongoing evaluation of learning
• **Student Self-Assessment:** Reflection on learning and growth
• **Parent Communication:** Regular updates on student progress
• **Data Collection:** Systematic tracking of improvement

### Success Metrics
• **Academic Performance:** Improvement in assessment scores
• **Engagement Levels:** Increased participation and motivation
• **Confidence Building:** Self-efficacy and positive attitude
• **Skill Development:** Mastery of targeted learning objectives
• **Long-term Retention:** Sustained learning over time

### Evaluation Timeline
• **2 Weeks:** Initial progress assessment
• **1 Month:** Mid-term evaluation and adjustment
• **2 Months:** Comprehensive progress review
• **3 Months:** Final evaluation and planning for next cycle

## IMPLEMENTATION PLAN

### Phase 1: Immediate Response (Week 1-2)
• Identify students for intensive intervention
• Begin small group instruction
• Implement additional practice opportunities
• Start parent communication
• Establish progress monitoring systems

### Phase 2: Targeted Intervention (Week 3-6)
• Expand intervention programs
• Implement differentiated instruction
• Begin peer tutoring programs
• Conduct regular progress assessments
• Adjust strategies based on results

### Phase 3: System Integration (Week 7-12)
• Integrate interventions into regular instruction
• Expand successful strategies
• Evaluate and refine approaches
• Plan for long-term sustainability
• Prepare for next assessment cycle

---
*Gap Analysis created by A2A School KPI System - Assessment Agent*
*Date: ${new Date().toLocaleDateString()}*
    `.trim();
  };

  const generateFeedbackCards = (data) => `# STUDENT FEEDBACK CARDS\n\n## Individual Feedback\n[Personalized feedback for each student based on their performance]`;

  const generateParentTemplates = (data) => `# PARENT COMMUNICATION TEMPLATES\n\n## Positive Progress Template\n[Template for communicating positive progress]\n\n## Areas for Improvement Template\n[Template for discussing areas needing attention]`;

  const generateStrengthsSummary = (data) => `# STRENGTHS & IMPROVEMENT SUMMARY\n\n## Class Strengths\n• [Overall class strengths]\n\n## Individual Strengths\n• [Individual student strengths]\n\n## Improvement Areas\n• [Areas for improvement]`;

  const generateGoalSheets = (data) => `# PERSONALIZED GOAL SHEETS\n\n[Individual learning goals for each student based on their current performance and needs]`;

  const generateGoalTracking = (data) => `# GOAL TRACKING TEMPLATES\n\n[Tracking sheets for monitoring student progress toward their individual goals]`;

  const generateAchievementTimeline = (data) => `# ACHIEVEMENT TIMELINE\n\n[Timeline showing when students are expected to achieve their learning goals]`;

  const generateSelfReflectionSheets = (data) => `# SELF-REFLECTION WORKSHEETS\n\n[Worksheets to help students reflect on their learning and identify areas for improvement]`;

  const generateQuestionPrompts = (data) => `# GUIDED QUESTION PROMPTS\n\n[Questions to guide student self-reflection and self-assessment]`;

  const generateSelfAssessmentRubric = (data) => `# SELF-ASSESSMENT RUBRIC\n\n[Rubric to help students assess their own learning and progress]`;

  const generateProgressDashboard = (data) => `# PROGRESS DASHBOARD\n\n[Visual representation of student progress across different learning objectives]`;

  const generateProgressSpreadsheet = (data) => `# PROGRESS TRACKING SPREADSHEET\n\n[Detailed spreadsheet for tracking individual student progress over time]`;

  const generateAlertGuidelines = (data) => `# ALERT SYSTEM GUIDELINES\n\n[Guidelines for identifying and responding to students who may be falling behind]`;

  const generateKPIPerformanceReport = (data) => `# TEACHER KPI PERFORMANCE REPORT\n\n## Key Performance Indicators\n• Student Achievement: ${Math.floor(Math.random() * 20) + 75}%\n• Engagement Level: ${Math.floor(Math.random() * 15) + 80}%\n• Assessment Quality: ${Math.floor(Math.random() * 10) + 85}%\n• Professional Development: ${Math.floor(Math.random() * 15) + 70}%`;

  const generateAchievementSummary = (data) => `# ACHIEVEMENT SUMMARY\n\n## Highlights\n• [Key achievements and successes]\n\n## Milestones Reached\n• [Important milestones achieved]\n\n## Recognition\n• [Areas deserving recognition]`;

  const generateImprovementRecommendations = (data) => `# IMPROVEMENT RECOMMENDATIONS\n\n## Professional Development\n• [Recommended training and development]\n\n## Teaching Strategies\n• [Suggested improvements to teaching methods]\n\n## Assessment Practices\n• [Recommendations for assessment improvements]`;

  const generateAdminReport = (data) => `# ADMINISTRATIVE REPORT\n\n## Executive Summary\n[High-level overview of performance and outcomes]\n\n## Detailed Analysis\n[Comprehensive analysis of all aspects of the educational process]`;

  const generateSchoolPerformanceSummary = (data) => `# SCHOOL PERFORMANCE SUMMARY\n\n## Overall Performance\n• Academic Achievement: ${Math.floor(Math.random() * 20) + 75}%\n• Student Engagement: ${Math.floor(Math.random() * 15) + 80}%\n• Teacher Effectiveness: ${Math.floor(Math.random() * 10) + 85}%`;

  const generateStrategicRecommendations = (data) => `# STRATEGIC RECOMMENDATIONS\n\n## Short-term Actions\n• [Immediate actions to take]\n\n## Long-term Strategies\n• [Long-term strategic initiatives]\n\n## Resource Allocation\n• [Recommendations for resource distribution]`;

  const generateActionPlan = (data) => `# ACTION PLAN TEMPLATE\n\n## Priority Actions\n1. [First priority action]\n2. [Second priority action]\n3. [Third priority action]\n\n## Timeline\n• Week 1: [Actions]\n• Week 2: [Actions]\n• Week 3: [Actions]`;

  const generateProcessImprovements = (data) => `# PROCESS IMPROVEMENT SUGGESTIONS\n\n## Current Process Analysis\n[Analysis of current processes]\n\n## Improvement Opportunities\n[Identified opportunities for improvement]\n\n## Implementation Plan\n[Plan for implementing improvements]`;

  const generateImplementationTimeline = (data) => `# IMPLEMENTATION TIMELINE\n\n## Phase 1 (Immediate)\n• [Immediate implementation items]\n\n## Phase 2 (Short-term)\n• [Short-term implementation items]\n\n## Phase 3 (Long-term)\n• [Long-term implementation items]`;

  // Function to handle document download
  const downloadDocument = (content, filename, type = 'text/plain') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Function to show output modal
  const showWorkflowOutput = (stepName, stepData) => {
    const output = generateWorkflowOutput(stepName, stepData);
    if (output) {
      setSelectedOutput(output);
      setShowOutputModal(true);
    }
  };

  // Function to update API usage tracking
  const updateApiUsage = (estimatedTokens = 500) => {
    const now = new Date();
    const today = now.toDateString();
    const lastRequestDate = apiUsage.lastRequest ? new Date(apiUsage.lastRequest).toDateString() : null;
    
    // Calculate costs (approximate rates based on DeepSeek pricing)
    const costPerToken = 0.000001; // Approximate cost per token
    const newCostCNY = estimatedTokens * costPerToken;
    const newCostUSD = newCostCNY * 0.14; // Approximate CNY to USD conversion
    
    setApiUsage(prev => ({
      totalRequests: prev.totalRequests + 1,
      totalTokens: prev.totalTokens + estimatedTokens,
      totalCostCNY: prev.totalCostCNY + newCostCNY,
      totalCostUSD: prev.totalCostUSD + newCostUSD,
      lastRequest: now.toISOString(),
      requestsToday: today === lastRequestDate ? prev.requestsToday + 1 : 1
    }));
  };

  // Render main dashboard
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Complete Education Lifecycle A2A</h1>
                <p className="text-sm text-gray-500">Teaching → Learning → Assessment → Feedback → Adaptation → Teacher Evaluation</p>
              </div>
            </div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-700">Full Cycle Demo</div>
                <div className="text-xs text-gray-500">12-step educational workflow</div>
              </div>
              <button
                onClick={isSimulationRunning ? stopSimulation : startFullCycle}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  isSimulationRunning 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSimulationRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                <span>{isSimulationRunning ? 'Running Cycle...' : 'Start Full Cycle'}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* DeepSeek API Usage Display */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">DeepSeek AI Active</span>
              </div>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-600">Requests:</span>
                  <span className="font-semibold text-blue-600">{apiUsage.totalRequests}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Brain className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-600">Tokens:</span>
                  <span className="font-semibold text-purple-600">{apiUsage.totalTokens.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-gray-600">Cost:</span>
                  <span className="font-semibold text-green-600">¥{apiUsage.totalCostCNY.toFixed(4)}</span>
                  <span className="text-gray-500">(${apiUsage.totalCostUSD.toFixed(4)})</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-gray-600">Today:</span>
                  <span className="font-semibold text-orange-600">{apiUsage.requestsToday}</span>
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {apiUsage.lastRequest ? `Last request: ${new Date(apiUsage.lastRequest).toLocaleTimeString()}` : 'No requests yet'}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {[
              { id: 'learn', label: 'Learn System', icon: <Lightbulb className="w-4 h-4" /> },
              { id: 'agents', label: 'Agent Ecosystem', icon: <Network className="w-4 h-4" /> },
              { id: 'workflow', label: 'Complete Workflow', icon: <CheckCircle className="w-4 h-4" /> },
              { id: 'visual', label: 'Visual Flow', icon: <GitBranch className="w-4 h-4" /> },
              { id: 'simulation', label: 'Live Simulation', icon: <Play className="w-4 h-4" /> },
              { id: 'api', label: 'API Testing', icon: <Zap className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Full Educational Cycle Simulation Banner */}
        {showSimulationBanner && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-xl text-white mb-8 relative">
            <button
              onClick={() => setShowSimulationBanner(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold mb-3">Full Educational Cycle Simulation</h2>
            <p className="text-blue-100 mb-4">
              Watch the complete educational workflow from teaching to teacher evaluation. This simulation demonstrates how agents collaborate to create a comprehensive, data-driven educational system.
            </p>
            <ul className="space-y-2 text-blue-50">
              <li className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>See students progress through learning, assessment, and feedback</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Watch adaptive goals being set based on individual performance</span>
              </li>
              <li className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Observe teacher KPI calculation and administrative review</span>
              </li>
            </ul>
          </div>
        )}

        {/* Tab-specific Content */}
        {activeTab === 'simulation' && (
          <>
            {/* KPI Metrics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { key: 'students', label: 'Students', icon: <Users className="w-6 h-6 text-blue-600" /> },
                { key: 'lessons', label: 'Lessons', icon: <BookOpen className="w-6 h-6 text-purple-600" /> },
                { key: 'tests', label: 'Tests', icon: <ClipboardCheck className="w-6 h-6 text-green-600" /> },
                { key: 'feedback', label: 'Feedback', icon: <MessageSquare className="w-6 h-6 text-orange-600" /> },
                { key: 'goals', label: 'Goals', icon: <Target className="w-6 h-6 text-pink-600" /> },
                { key: 'selfAssess', label: 'Self-Assess', icon: <Star className="w-6 h-6 text-red-600" /> },
                { key: 'teacherKpi', label: 'Teacher KPI', icon: <BarChart3 className="w-6 h-6 text-gray-600" /> }
              ].map(metric => (
                <div key={metric.key} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    {metric.icon}
                    <span className="text-2xl font-bold text-gray-900">{simulationData[metric.key]}</span>
                  </div>
                  <p className="text-xs font-medium text-gray-600">{metric.label}</p>
                </div>
              ))}
            </div>

            {/* Activity Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Activity Stream */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Agent Activity Stream</h2>
                  </div>
                  {isSimulationRunning && (
                    <div className="flex items-center space-x-2 text-sm text-blue-600">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                      <span>Live</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {agentLogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Start the simulation to see the complete workflow</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {agentLogs.slice(-10).map(log => (
                        <div key={log.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 bg-blue-500 rounded-full" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">{log.agent}</p>
                              <p className="text-xs text-gray-500">{log.timestamp}</p>
                            </div>
                            <p className="text-sm mt-1 text-gray-600">{log.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Current Workflow Step */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Current Workflow Step</h2>
                </div>
                <div className="p-6">
                  {!isSimulationRunning && currentStep === 0 && agentLogs.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Network className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Start the demo to see the educational workflow</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Step Header with Status */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                            workflowState[currentStep]?.status === 'completed' ? 'bg-green-600' :
                            workflowState[currentStep]?.status === 'in_progress' ? 'bg-blue-600' :
                            'bg-gray-400'
                          } text-white`}>
                            {currentStep + 1}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900">{workflowState[currentStep]?.title}</h3>
                            <p className="text-sm text-gray-600">{workflowState[currentStep]?.description}</p>
                          </div>
                        </div>
                        <div>
                          {workflowState[currentStep]?.status === 'completed' && (
                            <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-800 rounded-full">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs font-medium">Completed</span>
                            </div>
                          )}
                          {workflowState[currentStep]?.status === 'in_progress' && (
                            <div className="flex items-center space-x-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                              <Clock className="w-4 h-4 animate-pulse" />
                              <span className="text-xs font-medium">In Progress</span>
                            </div>
                          )}
                          {workflowState[currentStep]?.status === 'pending' && (
                            <div className="flex items-center space-x-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full">
                              <Clock className="w-4 h-4" />
                              <span className="text-xs font-medium">Pending</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Agent Action Message */}
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="flex items-start space-x-2">
                          <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900 mb-1">
                              Agent: {workflowState[currentStep]?.agent}
                            </p>
                            <p className="text-sm text-blue-700">{workflowState[currentStep]?.action}</p>
                          </div>
                        </div>
                      </div>

                      {/* Process Status Details */}
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-start space-x-2">
                          <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 mb-2">Process Status:</p>
                            <div className="space-y-1">
                              {workflowState[currentStep]?.status === 'in_progress' && (
                                <p className="text-sm text-gray-700">🔄 Agent is actively processing this step...</p>
                              )}
                              {workflowState[currentStep]?.status === 'completed' && (
                                <p className="text-sm text-green-700">✅ Step completed successfully. Moving to next step...</p>
                              )}
                              {workflowState[currentStep]?.status === 'pending' && (
                                <p className="text-sm text-gray-700">⏳ Waiting for previous steps to complete...</p>
                              )}
                              <p className="text-sm text-gray-600 mt-2">
                                Step {currentStep + 1} of 12: {workflowState[currentStep]?.title}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Workflow Progress */}
                      <div className="pt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-2">
                          <span>Overall Workflow Progress</span>
                          <span>{currentStep + 1} of 12 ({Math.round(((currentStep + 1) / 12) * 100)}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${((currentStep + 1) / 12) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'learn' && (
          <LearnSystemView />
        )}

        {activeTab === 'agents' && (
          <AgentEcosystemView />
        )}

        {activeTab === 'workflow' && (
          <CompleteWorkflowView />
        )}

        {activeTab === 'visual' && (
          <VisualFlowTab
            a2aClient={null}
            isRunning={workflowExecuting}
            setIsRunning={setWorkflowExecuting}
          />
        )}

        {activeTab === 'api' && (
          <div className="space-y-8">
            {/* API Testing Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">A2A Workflow Testing Center</h2>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Backend Connected</span>
                </div>
              </div>
              <p className="text-gray-600">
                Test all 12 workflow steps with real DeepSeek AI integration. Execute individual steps or run the complete sequential workflow.
              </p>
            </div>

            {/* DeepSeek API Usage Details */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-blue-600" />
                  <span>DeepSeek AI Usage Statistics</span>
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600 font-medium">Live Tracking</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Total Requests</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">{apiUsage.totalRequests}</div>
                  <div className="text-xs text-gray-500">API calls made</div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Brain className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-600">Total Tokens</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">{apiUsage.totalTokens.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Tokens consumed</div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-600">Total Cost</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">¥{apiUsage.totalCostCNY.toFixed(4)}</div>
                  <div className="text-xs text-gray-500">${apiUsage.totalCostUSD.toFixed(4)} USD</div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-600">Today's Requests</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">{apiUsage.requestsToday}</div>
                  <div className="text-xs text-gray-500">Since midnight</div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2 text-sm text-blue-800">
                  <Info className="w-4 h-4" />
                  <span className="font-medium">Real-time API Integration:</span>
                  <span>Each workflow step makes live calls to DeepSeek AI, generating realistic educational content and tracking usage automatically.</span>
                </div>
              </div>
            </div>

            {/* Workflow Control Panel */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Control Panel</h3>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={executeSequentialWorkflow}
                    disabled={workflowExecuting}
                    className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="w-4 h-4" />
                    <span>{workflowExecuting ? 'Executing...' : 'Start Sequential Workflow'}</span>
                  </button>
                  
                  {workflowExecuting && (
                    <button
                      onClick={stopWorkflow}
                      className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Pause className="w-4 h-4" />
                      <span>Stop Workflow</span>
                    </button>
                  )}
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-gray-600">Progress</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {workflowStep}/{workflowSteps.length}
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(workflowStep / workflowSteps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Basic API Endpoints */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic API Endpoints</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                    onClick={testHealth}
                    disabled={apiLoading['/health']}
                    className="w-full flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Health Check</p>
                        <p className="text-sm text-gray-600">GET /health</p>
                      </div>
                    </div>
                    {apiLoading['/health'] ? (
                      <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  <button
                    onClick={discoverCapabilities}
                    disabled={apiLoading['/a2a/discover']}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <Zap className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Discover Capabilities</p>
                        <p className="text-sm text-gray-600">GET /a2a/discover</p>
                      </div>
                    </div>
                    {apiLoading['/a2a/discover'] ? (
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  <button
                    onClick={testUXNegotiation}
                    disabled={apiLoading['/a2a/negotiate-ux']}
                    className="w-full flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <MessageSquare className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">UX Negotiation</p>
                        <p className="text-sm text-gray-600">GET /a2a/negotiate-ux</p>
                      </div>
                    </div>
                    {apiLoading['/a2a/negotiate-ux'] ? (
                      <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
              </div>
            </div>

            {/* Individual Workflow Steps */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Individual Workflow Steps</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workflowSteps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => executeWorkflowStep(step)}
                    disabled={apiLoading[step.name] || workflowExecuting}
                    className={`p-4 bg-gradient-to-br from-${step.color}-50 to-${step.color}-100 border border-${step.color}-200 rounded-lg hover:from-${step.color}-100 hover:to-${step.color}-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-8 h-8 bg-${step.color}-500 rounded-full flex items-center justify-center`}>
                        {step.icon}
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-gray-900">{step.title}</p>
                        <p className="text-sm text-gray-600">Step {step.id}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">{step.description}</p>
                    {apiLoading[step.name] && (
                      <div className={`w-5 h-5 border-2 border-${step.color}-500 border-t-transparent rounded-full animate-spin mx-auto`}></div>
                    )}
                    {workflowResults[step.name] && (
                      <div className="flex items-center justify-center space-x-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs">Completed</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Workflow Outputs - Teacher Documents */}
            {Object.keys(workflowOutputs).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span>Generated Teacher Documents</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(workflowOutputs).map(([stepName, output]) => (
                    <div key={stepName} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                          {output.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{output.title}</h4>
                          <p className="text-sm text-gray-600">Ready for download</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {output.documents.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between bg-white rounded-lg p-2 border border-gray-200">
                            <div className="flex items-center space-x-2">
                              {doc.icon}
                              <span className="text-sm font-medium text-gray-700">{doc.name}</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{doc.type}</span>
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => {
                                  setSelectedOutput({...output, selectedDoc: doc});
                                  setShowOutputModal(true);
                                }}
                                className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => downloadDocument(doc.content, `${doc.name}.txt`, 'text/plain')}
                                className="p-1 text-green-600 hover:text-green-800 transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Workflow Execution Log */}
            {workflowLogs.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Execution Log</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {workflowLogs.map((log) => (
                    <div key={log.id} className={`p-3 rounded-lg border ${
                      log.status === 'completed' ? 'bg-green-50 border-green-200' :
                      log.status === 'failed' ? 'bg-red-50 border-red-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">Step {log.step}: {log.title}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            log.status === 'completed' ? 'bg-green-100 text-green-800' :
                            log.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {log.status}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{log.timestamp}</span>
                      </div>
                      <p className="text-sm text-gray-600">{log.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* API Response Viewer */}
            {Object.keys(apiResults).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">API Response Details</h3>
                <div className="space-y-4">
                  {Object.entries(apiResults).slice(-5).reverse().map(([endpoint, result]) => (
                    <div key={endpoint} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{endpoint}</span>
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              result.status === 200 ? 'bg-green-100 text-green-800' :
                              result.status === 'ERROR' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {result.status} {result.statusText}
                            </span>
                            <span className="text-xs text-gray-500">{result.timestamp}</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Document Preview Modal */}
      {showOutputModal && selectedOutput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  {selectedOutput.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedOutput.title}</h3>
                  <p className="text-sm text-gray-600">Document Preview</p>
                </div>
              </div>
              <button
                onClick={() => setShowOutputModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {selectedOutput.selectedDoc ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      {selectedOutput.selectedDoc.icon}
                      <h4 className="font-medium text-gray-900">{selectedOutput.selectedDoc.name}</h4>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selectedOutput.selectedDoc.type}</span>
                    </div>
                    <button
                      onClick={() => downloadDocument(selectedOutput.selectedDoc.content, `${selectedOutput.selectedDoc.name}.txt`, 'text/plain')}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                      {selectedOutput.selectedDoc.content}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">Select a document to preview:</p>
                  <div className="space-y-2">
                    {selectedOutput.documents.map((doc, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedOutput({...selectedOutput, selectedDoc: doc})}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          {doc.icon}
                          <span className="font-medium text-gray-900">{doc.name}</span>
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">{doc.type}</span>
                        </div>
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      <div className="fixed top-4 right-4 space-y-2 z-50">
        {notifications.map(notification => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onRemove={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default App;
